import * as i0 from '@angular/core';
import { Component, ViewEncapsulation, ChangeDetectionStrategy, Input, Injectable, EventEmitter, Output, InjectionToken, Inject, NgModule } from '@angular/core';
import * as i1 from '@play/common';
import { CommonsLocationModule, CommonsComponentModule, SvgIconsModule, CommonsErrorModule } from '@play/common';
import { map, filter, maxBy, forEach, uniqBy, find, findLast, indexOf, isUndefined, reduce, first, sortBy, last, slice, assign, forEachRight, includes, keyBy } from 'lodash';
import * as i1$2 from '@ngrx/store';
import * as i4$1 from '@play/cup-page';
import { SelectParticipantAction, isCheckedIn, getSelectedParticipant, CupPageModule, InitSelectedParticipantAction } from '@play/cup-page';
import { isTeam, isRosterEnabled } from '@play/utility/league';
import * as i1$1 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i3 from '@play/translate';
import { TranslationModule } from '@play/translate';
import * as i4 from '@play/premium';
import { PremiumModule } from '@play/premium';
import { playersToDictionary } from '@play/utility/player';
import { LoadTeamsOnceAction, getTeams, LoadPlayersOnceAction, getPlayers, LoadTeamOnceAction, getTeam, LoadPlayerOnceAction, getPlayer, LoadLeagueAction, getLeague, entryAvailable, LoadParticipantsOnceAction, getLeagueParticipants, exists, LoadLeagueResultsOnceAction, getLeagueResults, LoadLeagueOnceAction, getUser, LoadContestantsOnceAction, getContestants, StateModule } from '@play/state';
import { of, combineLatest } from 'rxjs';
import { teamsToDictionary } from '@play/utility/team';
import { filter as filter$1, map as map$1, switchMap } from 'rxjs/operators';

const _c0$d = ["*"];
class MatchLinkComponent {
    constructor(linkService) {
        this.linkService = linkService;
    }
    ngOnChanges(changes) {
        this.matchUrl = this.linkService.match(this.leagueUri, this.matchId);
    }
}
MatchLinkComponent.ɵfac = function MatchLinkComponent_Factory(t) { return new (t || MatchLinkComponent)(i0.ɵɵdirectiveInject(i1.LinkService)); };
MatchLinkComponent.ɵcmp = i0.ɵɵdefineComponent({ type: MatchLinkComponent, selectors: [["path-view-match-link"]], inputs: { leagueUri: "leagueUri", matchId: "matchId" }, features: [i0.ɵɵNgOnChangesFeature], ngContentSelectors: _c0$d, decls: 2, vars: 1, consts: [[3, "locationHref"]], template: function MatchLinkComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵprojectionDef();
        i0.ɵɵelementStart(0, "a", 0);
        i0.ɵɵprojection(1);
        i0.ɵɵelementEnd();
    } if (rf & 2) {
        i0.ɵɵproperty("locationHref", ctx.matchUrl);
    } }, directives: [i1.LocationDirective], encapsulation: 2, changeDetection: 0 });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(MatchLinkComponent, [{
        type: Component,
        args: [{
                selector: "path-view-match-link",
                templateUrl: "./match-link.component.html",
                encapsulation: ViewEncapsulation.Emulated,
                changeDetection: ChangeDetectionStrategy.OnPush,
            }]
    }], function () { return [{ type: i1.LinkService }]; }, { leagueUri: [{
            type: Input
        }], matchId: [{
            type: Input
        }] }); })();

class DoubleEliminationService {
    /**
     * @param match The match that should be checked
     * @param matches Matches must be passed in traversable order.
     */
    isLowerBracketFinal(match, matches) {
        return matches.filter(tMatch => tMatch.bracket === 1).pop() === match;
    }
    /**
     * @param match
     * @param matches Matches must be passed in traversable order.
     */
    isUpperBracketFinal(match, matches) {
        if (this.isDoubleDoubleFinalCupMode(matches)) {
            return match === matches[matches.length - 5];
        }
        return match === matches[matches.length - 4];
    }
    /**
     * Gets the first grind final in double elimination tournament
     * Works for simple double elimination and double double final
     *
     * @param matches Matches must be passed in traversable order.
     */
    getFirstGrandFinal(matches) {
        if (this.isDoubleDoubleFinalCupMode(matches)) {
            return matches[matches.length - 2];
        }
        return matches[matches.length - 1];
    }
    /**
     * Gets the last grind final in double elimination tournament
     * Works for simple double elimination and double double final
     *
     * @param matches Matches must be passed in traversable order.
     */
    getLastGrandFinal(matches) {
        return matches[matches.length - 1];
    }
    /**
     * Checks if a match is a grandfinal in a double elimination tournament
     *
     * @param match
     * @param matches Matches must be passed in traversable order.
     */
    isGrandFinal(match, matches) {
        if (this.isDoubleDoubleFinalCupMode(matches)) {
            return match === matches[matches.length - 1] || match === matches[matches.length - 2];
        }
        return match === matches[matches.length - 1];
    }
    /**
     * @param matches Matches must be passed in traversable order.
     */
    isDoubleDoubleFinalCupMode(matches) {
        if (matches.length <= 5) {
            return false;
        }
        const upperBracket = [matches[matches.length - 1].bracket, matches[matches.length - 2].bracket, matches[matches.length - 5].bracket].filter(bracket => bracket === 0).length === 3;
        const lowerBracket = [matches[matches.length - 3].bracket, matches[matches.length - 4].bracket].filter(bracket => bracket === 1).length === 2;
        return upperBracket && lowerBracket;
    }
    /**
     * @param matches
     */
    isDoubleEliminationCupMode(matches) {
        if (matches.length < 3) {
            return false;
        }
        let amountOfLowerBracketMatches = 0;
        matches.some(match => {
            if (match.bracket === 1) {
                amountOfLowerBracketMatches++;
            }
            return amountOfLowerBracketMatches >= 2;
        });
        // Double Elimination always have atleast 2 matches in the lower bracket
        return amountOfLowerBracketMatches >= 2;
    }
}
DoubleEliminationService.ɵfac = function DoubleEliminationService_Factory(t) { return new (t || DoubleEliminationService)(); };
DoubleEliminationService.ɵprov = i0.ɵɵdefineInjectable({ token: DoubleEliminationService, factory: DoubleEliminationService.ɵfac });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DoubleEliminationService, [{
        type: Injectable
    }], null, null); })();

const setContestantToPlaying = (eliminatedContestants) => {
    return (id) => {
        const index = eliminatedContestants.indexOf(id);
        if (index !== -1) {
            eliminatedContestants.splice(index, 1);
        }
        return eliminatedContestants;
    };
};
class MatchService {
    constructor(doubleElimination) {
        this.doubleElimination = doubleElimination;
    }
    /**
     * Get an array of all eliminated contestants ids.
     * @param matches
     * @param participants
     */
    getEliminatedContestants(matches, participants) {
        if (!matches || !matches.length || !participants) {
            return [];
        }
        const eliminatedContestants = map(participants, (participant) => participant.id);
        const openMatches = filter(matches, match => match.state === "open");
        const lastRound = maxBy(matches, (match) => match.round).round;
        let lastRoundMatches = filter(matches, match => match.bracket === 0 && match.round === lastRound);
        if (this.doubleElimination.isDoubleDoubleFinalCupMode(matches)) {
            const secondGrandFinal = this.doubleElimination.getLastGrandFinal(matches);
            if (secondGrandFinal.state === "cancelled") {
                const firstGrandFinal = this.doubleElimination.getFirstGrandFinal(matches);
                lastRoundMatches = [firstGrandFinal];
            }
        }
        const setToPlaying = setContestantToPlaying(eliminatedContestants);
        forEach(openMatches, (match) => {
            // Everyone that still has a match open is not eliminated
            forEach(match.participants, (contestant) => {
                if (contestant.id === 0) {
                    return;
                }
                setToPlaying(contestant.id);
            });
        });
        forEach(lastRoundMatches, (match) => {
            // Everyone that still has a match open is not eliminated
            forEach(match.participants, (contestant) => {
                if (contestant.id === 0 || contestant.place !== 1) {
                    return;
                }
                setToPlaying(contestant.id);
            });
        });
        if (eliminatedContestants.length === Object.keys(participants).length) {
            const brackets = uniqBy(matches, (match) => match.bracket);
            brackets.forEach(bracket => {
                const finishedMatches = filter(matches, match => match.state === "closed" && match.bracket === bracket.bracket);
                const highestRound = maxBy(finishedMatches, (match) => match.round);
                const lastMatches = filter(finishedMatches, match => match.round === highestRound.round && match.bracket === highestRound.bracket);
                forEach(lastMatches, match => {
                    forEach(match.participants, (contestant) => {
                        if (contestant.id === 0 || contestant.place !== 1) {
                            return;
                        }
                        setToPlaying(contestant.id);
                    });
                });
            });
        }
        return eliminatedContestants;
    }
    /**
     * Get the index of a contestant in the match
     * @param match
     * @param contestantId
     */
    getContestantIndexOfMatch(match, contestantId) {
        if (match.participants[0].id === contestantId) {
            return 0;
        }
        if (match.participants[1].id === contestantId) {
            return 1;
        }
        if (match.participants[0].id) {
            return 1;
        }
        return 0;
    }
    /**
     * Get the match of a contestant in specified bracket/round.
     * Gets the match the contestant would be in (assuming only wins) if the match is in the future.
     * @param matches
     * @param bracket
     * @param round
     * @param contestantId
     */
    getMatch(matches, bracket, round, contestantId) {
        let res = find(matches, (match) => {
            if (match.bracket !== bracket || match.round !== round) {
                return false;
            }
            return !!find(match.participants, (contestant) => contestant.id === contestantId);
        });
        if (!res) {
            res = this.getFutureMatch(matches, round, contestantId);
        }
        return res;
    }
    /**
     * @param matches Matches must be passed in traversable order.
     */
    isDoubleElimination(matches) {
        return this.doubleElimination.isDoubleEliminationCupMode(matches);
    }
    /**
     * True if tournament has third place match.
     *
     * Matches must be passed in traversable order.
     *
     * @param matches
     */
    hasThirdPlaceMatch(matches) {
        // third place matches have the third place match right behind the upper bracket finals
        return matches.length && matches[0].bracket < matches[matches.length - 1].bracket;
    }
    /**
     * Get the upper bracket match of a lower bracket match that receives an upper bracket player.
     * Matches must be passed in traversable order.
     * @param matches       All matches in traversable order.
     * @param matchId       Id of a lower bracket match that receives an upper bracket loser.
     * @returns             The upper bracket match that matches the lower bracket matchId.
     */
    calculateUpperBracketMatch(matches, matchId) {
        // 1. Get match.
        const lowerBracketMatch = find(matches, match => match.id === matchId);
        const newBracket = lowerBracketMatch.bracket - 1;
        const newRound = lowerBracketMatch.round - 1;
        // getting round size so ugly, refactor?
        const round = filter(matches, (match) => {
            return match.round === lowerBracketMatch.round && match.bracket === lowerBracketMatch.bracket;
        });
        let roundSize = 1;
        while (roundSize < round.length) {
            roundSize *= 2;
        }
        roundSize *= 2;
        const newPos = this.lowerUpperMap(roundSize)[lowerBracketMatch.position];
        const upperBracketMatch = find(matches, (match) => {
            return match.bracket === newBracket && match.round === newRound && match.position === newPos;
        });
        if (upperBracketMatch) {
            return upperBracketMatch.id;
        }
        else {
            return undefined;
        }
    }
    /**
     * Generate a map of (lower-bracket-round-2-pos => real-pos-the-eye-sees),
     * for the given round size. This map can be used on all even rounds. Basically all rounds
     * that receive an upper bracket loser (minutes first round lower bracket).
     * @param ro        The bracket size to generate a map for (e.g. 2, 4, 8, 16, 32...).
     * @returns         A map that can be used to find out lower bracket matches real pos.
     */
    lowerUpperMap(ro) {
        // base case
        if (ro === 2) {
            return { 1: 1 };
        }
        const res = {};
        const prevLowerUpperMap = this.lowerUpperMap(ro / 2);
        /**
         * There are two modes.
         * If log(ro) / log(2) is odd add previous lowerUpperMap to head, else add to tail.
         */
        let pos = (Math.log(ro) / Math.log(2)) % 2 !== 0 ? 1 : ro / 4 + 1;
        for (let i = 0; i < ro / 4; i++, pos++) {
            res[pos] = prevLowerUpperMap[i + 1];
        }
        // Now mirror the other half.
        if (pos === ro / 2 + 1) {
            // start iterating backwards
            pos = ro / 4;
            for (let i = 0; i < ro / 4; i++, pos--) {
                // get mirror value
                res[pos] = ro / 2 + 1 - res[ro / 4 + i + 1];
            }
        }
        else {
            pos = ro / 4 + 1;
            for (let i = 0; i < ro / 4; i++, pos++) {
                res[pos] = ro / 2 + 1 - res[ro / 4 - i];
            }
        }
        return res;
    }
    /**
     * Get the next match of given match (winner).
     * @param matches       All matches in traversable order.
     * @param matchId       Id of match to get next match for.
     * @returns             The match, the winner of the given match would advance to.
     */
    getNextMatch(matches, matchId) {
        // 1. Find match.
        const currentMatch = find(matches, match => match.id === matchId);
        if (this.doubleElimination.isDoubleEliminationCupMode(matches)) {
            return this.getNextMatchDoubleElim(matches, currentMatch);
        }
        else {
            return this.getNextMatchSingleElim(matches, currentMatch);
        }
    }
    /**
     * Get parent matches of given match.
     * No parents === [] === first round.
     * @param matches       All matches in traversable order.
     * @param matchId       Id of match to get parents for.
     * @returns             Parents of the given match.
     */
    getParents(matches, matchId) {
        // 1. Find match.
        const currentMatch = find(matches, match => match.id === matchId);
        if (this.doubleElimination.isDoubleEliminationCupMode(matches)) {
            return this.getParentsDoubleElimBruteForce(matches, currentMatch);
        }
        return this.getParentsSingleElim(matches, currentMatch);
    }
    /**
     * Get the match a contestant would end up in a given round.
     * @param matches       All matches in traversable order.
     * @param round         The round to get the match for.
     * @param contestantId  Id of contestant to get the match for.
     */
    getFutureMatch(matches, round, contestantId) {
        // Find the most advanced match of contestant (most right left).
        let currentMatch = findLast(matches, (match) => {
            return !!find(match.participants, (participant) => {
                return participant.id === contestantId;
            });
        });
        // Traverse up until we find match with contestant in correct round.
        if (currentMatch && currentMatch.round !== round) {
            while (currentMatch && currentMatch.round < round) {
                currentMatch = this.getNextMatch(matches, currentMatch.id);
            }
        }
        return currentMatch;
    }
    /**
     * Get the next match of a given match for a single elimination bracket.
     * @param matches       All matches in traversable order.
     * @param currentMatch  Match to get next match for.
     * @returns             The match, the winner of the given match would advance to.
     */
    getNextMatchSingleElim(matches, currentMatch) {
        const nextRound = currentMatch.round + 1;
        const nextPosition = Math.ceil(currentMatch.position / 2);
        return find(matches, match => match.round === nextRound && match.position === nextPosition);
    }
    /**
     * Get the next match of given match (winner).
     * Expects double elim bracket.
     * @param matches       All matches in traversable order.
     * @param currentMatch  Match to get next match for.
     * @returns             The match, the winner of the given match would advance to.
     */
    getNextMatchDoubleElim(matches, currentMatch) {
        // Upper bracket.
        if (currentMatch.bracket === 0) {
            if (this.doubleElimination.isUpperBracketFinal(currentMatch, matches)) {
                return this.doubleElimination.getFirstGrandFinal(matches);
            }
            if (this.doubleElimination.isDoubleDoubleFinalCupMode(matches)) {
                if (currentMatch === this.doubleElimination.getFirstGrandFinal(matches)) {
                    return this.doubleElimination.getLastGrandFinal(matches);
                }
            }
            // First round.
            if (currentMatch.round === matches[0].round) {
                return this.getNextMatchSingleElim(matches, currentMatch);
            }
            return find(matches, match => match.round === currentMatch.round + 2 && match.position === Math.ceil(currentMatch.position / 2));
        }
        // Lower bracket final.
        if (this.doubleElimination.isLowerBracketFinal(currentMatch, matches)) {
            return this.doubleElimination.getFirstGrandFinal(matches);
        }
        // Upper bracket receiver.
        if (currentMatch.round % 2 === matches[0].round) {
            return find(matches, (match) => {
                return (match.bracket === 1 && match.round === currentMatch.round + 1 && match.position === Math.ceil(currentMatch.position / 2));
            });
        }
        return find(matches, (match) => {
            return match.bracket === 1 && match.round === currentMatch.round + 1 && match.position === currentMatch.position;
        });
    }
    /**
     * Get parent matches of given match for single elimination.
     * No parents === [] === first round.
     * @param matches       All matches in traversable order.
     * @param currentMatch  Match to get parents for.
     * @returns             Parents of the given match.
     */
    getParentsSingleElim(matches, currentMatch) {
        const previousRound = currentMatch.round - 1;
        const previousPositions = [currentMatch.position * 2, currentMatch.position * 2 - 1];
        // Third place match needs special care.
        if (this.hasThirdPlaceMatch(matches) && currentMatch.round === matches[matches.length - 1].round) {
            return filter(matches, (match) => {
                return match.round === currentMatch.round - 1;
            });
        }
        return filter(matches, match => {
            return match.round === previousRound && (match.position === previousPositions[0] || match.position === previousPositions[1]);
        });
    }
    /**
     * Get the parents of a given match.
     * Expects double elim bracket.
     * Same as getParentsDoubleElim, but using a brute force method > low performance.
     * @param matches       All matches in traversable order.
     * @param currentMatch  Match to get parents for.
     * @returns             Parents of the given match.
     */
    getParentsDoubleElimBruteForce(matches, currentMatch) {
        const firstRound = matches[0].round;
        const parents = [];
        /* Case: first round upper bracket. No parents. */
        if (currentMatch.round === firstRound) {
            return [];
        }
        /* Grandfinal 2 should be ignored and grandfinal 1 should be used */
        if (this.doubleElimination.isDoubleDoubleFinalCupMode(matches)) {
            if (currentMatch === this.doubleElimination.getLastGrandFinal(matches)) {
                return this.getParentsDoubleElimBruteForce(matches, this.doubleElimination.getFirstGrandFinal(matches));
            }
        }
        /* Case: upper bracket, non final. */
        if (currentMatch.bracket === 0 && !this.doubleElimination.isGrandFinal(currentMatch, matches)) {
            // Upper bracket minus final is same as single elim.
            return this.getParentsDoubleElimUpperBracket(matches, currentMatch);
        }
        /* Case: lower bracket, first round. */
        if (currentMatch.bracket === 1 && currentMatch.round === firstRound + 1) {
            return this.getParentsLowerBracketFirstRound(matches, currentMatch);
        }
        /* Case: lower bracket, ub-droput-receiver. */
        if (currentMatch.round % 2 === firstRound) {
            return this.getParentsDropoutReceiver(matches, currentMatch);
        }
        /* Case: other matches: brute force! */
        for (let i = 0; i < matches.length; i++) {
            const nextMatch = this.getNextMatch(matches, matches[i].id);
            if (nextMatch && nextMatch.id === currentMatch.id) {
                parents.push(matches[i]);
            }
        }
        return parents;
    }
    /**
     * Gets the parents of a double elim match in upper bracket.
     * Difference to single elim is that the previous round, if not the second round,
     * will be currentRound - 2.
     * Also no third place matches.
     * @param matches       All matches in traversable order.
     * @param currentMatch  The match to get parents from.
     * @returns Parents of the given currentMatch.
     */
    getParentsDoubleElimUpperBracket(matches, currentMatch) {
        const previousRound = currentMatch.round - (currentMatch.round === matches[0].round + 1 ? 1 : 2);
        const previousPositions = [currentMatch.position * 2, currentMatch.position * 2 - 1];
        return filter(matches, match => {
            return (match.round === previousRound &&
                match.bracket === currentMatch.bracket &&
                (match.position === previousPositions[0] || match.position === previousPositions[1]));
        });
    }
    /**
     * True if supplied state string is closed like.
     * Closed like states are [closed, cancelled].
     */
    isClosedLike(matchState) {
        return matchState === "closed" || matchState === "cancelled";
    }
    /**
     * Gets the parents of a double elim match in the first round of lower bracket.
     * @param matches       All matches in traversable order.
     * @param currentMatch  The match to get parents from.
     * @returns Parents of the given currentMatch.
     */
    getParentsLowerBracketFirstRound(matches, currentMatch) {
        const parents = [];
        const firstRound = matches[0].round;
        const round = filter(matches, (match) => match.round === firstRound);
        // getting round size so ugly, refactor?
        let roundSize = 1;
        while (roundSize < round.length) {
            roundSize *= 2;
        }
        roundSize *= 2;
        const luMap = this.lowerUpperMap(roundSize);
        const realPos = currentMatch.position;
        const slots = [2 * realPos - 1, 2 * realPos];
        forEach(slots, slot => {
            parents.push(find(matches, (match) => match.round === firstRound && match.position === luMap[slot]));
        });
        return parents;
    }
    /**
     * Gets the parents of a double elim match of a dropout receiver (match that receives an upper bracket loser).
     * @param matches       All matches in traversable order.
     * @param currentMatch  The match to get parents from.
     * @returns Parents of the given currentMatch.
     */
    getParentsDropoutReceiver(matches, currentMatch) {
        const parents = [];
        parents.push(find(matches, (match) => match.id === this.calculateUpperBracketMatch(matches, currentMatch.id)));
        for (let i = 0; i < matches.length; i++) {
            const nextMatch = this.getNextMatch(matches, matches[i].id);
            if (nextMatch && nextMatch.id === currentMatch.id) {
                parents.push(matches[i]);
            }
        }
        return parents;
    }
}
MatchService.ɵfac = function MatchService_Factory(t) { return new (t || MatchService)(i0.ɵɵinject(DoubleEliminationService)); };
MatchService.ɵprov = i0.ɵɵdefineInjectable({ token: MatchService, factory: MatchService.ɵfac });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(MatchService, [{
        type: Injectable
    }], function () { return [{ type: DoubleEliminationService }]; }, null); })();

class RoundState {
    constructor(states) {
        this.states = states;
    }
    isEliminated() {
        return indexOf(this.states, RoundState.ELIMINATED) !== -1;
    }
    isCancelled() {
        return indexOf(this.states, RoundState.CANCELLED) !== -1;
    }
    isWin() {
        return indexOf(this.states, RoundState.WIN) !== -1;
    }
    isLoss() {
        return indexOf(this.states, RoundState.LOSS) !== -1;
    }
    isCurrent() {
        return indexOf(this.states, RoundState.CURRENT) !== -1;
    }
    isFollowing() {
        return indexOf(this.states, RoundState.FOLLOWING) !== -1;
    }
    isFuture() {
        return indexOf(this.states, RoundState.FUTURE) !== -1;
    }
    isFinal() {
        return indexOf(this.states, RoundState.FINAL) !== -1;
    }
}
RoundState.ELIMINATED = "ELIMINATED";
RoundState.CANCELLED = "CANCELLED";
RoundState.FOLLOWING = "FOLLOWING";
RoundState.FUTURE = "FUTURE";
RoundState.CURRENT = "CURRENT";
RoundState.WIN = "WIN";
RoundState.LOSS = "LOSS";
RoundState.FINAL = "FINAL";
class RoundService {
    constructor(matchService) {
        this.matchService = matchService;
    }
    /**
     * Get top percentile of contestants in the given round
     * Percentage: the top percentile of participants within a given round.
     * Count: the amount contestants that dropped out in rounds earlier than the given round.
     *
     * @param matches
     * @param round
     */
    getRoundStats(matches, round) {
        const participantAmountFromMatches = m => Object.keys(m.reduce((output, match) => {
            match.participants.forEach(participant => {
                // Don't count empty slot / 0.
                if (participant.id) {
                    output[participant.id] = 1;
                }
            });
            return output;
        }, {})).length;
        const betterOrEqualMatches = matches.filter(match => match.round >= round.round);
        const allParticipantsAmount = participantAmountFromMatches(matches);
        const betterOrEqualParticipantsAmount = participantAmountFromMatches(betterOrEqualMatches);
        return {
            percentage: Math.floor((betterOrEqualParticipantsAmount / allParticipantsAmount) * 100),
            count: Math.max(allParticipantsAmount - betterOrEqualParticipantsAmount, 0),
        };
    }
    /**
     * Checks if a round is a third place match
     * @param round
     * @param tournament
     */
    isThirdPlaceRound(round, tournament) {
        return round && round.bracket === 1 && round.of === 2 && !tournament.isDoubleElimination;
    }
    /**
     * @param contestantId
     * @param match
     * @param round
     * @param nextRound
     */
    getRoundState(contestantId, match, round, nextRound) {
        const states = [];
        if (!find(match.participants, (participant) => participant.id === round.contestantId)) {
            states.push(RoundState.FUTURE);
        }
        else if (!this.matchService.isClosedLike(match.state)) {
            states.push(RoundState.CURRENT);
        }
        else if (match.state === "cancelled") {
            states.push(RoundState.CANCELLED);
        }
        else {
            const contestantIndex = this.matchService.getContestantIndexOfMatch(match, round.contestantId);
            if (match.participants[contestantIndex].place === 1) {
                states.push(RoundState.WIN);
            }
            else {
                states.push(RoundState.LOSS);
            }
        }
        if (round.isFinal) {
            states.push(RoundState.FINAL);
        }
        if (!isUndefined(nextRound) && round.contestantId !== nextRound.contestantId) {
            states.push(RoundState.ELIMINATED);
        }
        if (round.contestantId !== contestantId) {
            states.push(RoundState.FOLLOWING);
        }
        return new RoundState(states);
    }
    /**
     * Get amount of contestants within specific round.
     * @param matches
     * @param bracket
     * @param round
     */
    getAmountOfContestants(matches, bracket, round) {
        let amount = filter(matches, match => match.bracket === bracket && match.round === round).length * 2; // disabled because of intended bitwise operator
        //Since we have no information of deleted matches we need to round it up to the next power of 2
        // eslint-disable-next-line no-bitwise
        if (amount && (amount & (amount - 1)) !== 0) {
            // Check if not power of 2
            const closestPowerOf2 = Math.pow(2, Math.round(Math.log(amount) / Math.log(2)));
            if (amount > closestPowerOf2) {
                amount = closestPowerOf2 * 2;
            }
            else {
                amount = closestPowerOf2;
            }
        }
        /* tslint:enable */
        return amount;
    }
}
RoundService.ɵfac = function RoundService_Factory(t) { return new (t || RoundService)(i0.ɵɵinject(MatchService)); };
RoundService.ɵprov = i0.ɵɵdefineInjectable({ token: RoundService, factory: RoundService.ɵfac });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(RoundService, [{
        type: Injectable
    }], function () { return [{ type: MatchService }]; }, null); })();

class TournamentInformation {
    constructor(isDoubleElimination, hasThirdPlaceMatch, isTeam, bracketSize) {
        this.isDoubleElimination = isDoubleElimination;
        this.hasThirdPlaceMatch = hasThirdPlaceMatch;
        this.isTeam = isTeam;
        this.bracketSize = bracketSize;
    }
}
class TournamentService {
    constructor(roundService, matchService) {
        this.roundService = roundService;
        this.matchService = matchService;
    }
    /**
     * @param matches
     * @param league
     */
    getTournamentInformation(matches, league) {
        const isDoubleElimination = this.matchService.isDoubleElimination(matches);
        const hasThirdPlace = this.matchService.hasThirdPlaceMatch(matches);
        const isTeam$1 = isTeam(league);
        let bracketSize = 0;
        if (matches.length) {
            bracketSize = this.roundService.getAmountOfContestants(matches, matches[0].bracket, matches[0].round);
        }
        return new TournamentInformation(isDoubleElimination, hasThirdPlace, isTeam$1, bracketSize);
    }
}
TournamentService.ɵfac = function TournamentService_Factory(t) { return new (t || TournamentService)(i0.ɵɵinject(RoundService), i0.ɵɵinject(MatchService)); };
TournamentService.ɵprov = i0.ɵɵdefineInjectable({ token: TournamentService, factory: TournamentService.ɵfac });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(TournamentService, [{
        type: Injectable
    }], function () { return [{ type: RoundService }, { type: MatchService }]; }, null); })();

const _c0$c = function (a0, a1) { return { of: a0, first: a1 }; };
function MatchRoundOfComponent_ng_container_1_span_1_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "span");
    i0.ɵɵtext(1);
    i0.ɵɵpipe(2, "translate");
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r2 = i0.ɵɵnextContext(2);
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate1(" ", i0.ɵɵpipeBind2(2, 1, "path-view:lower-round-of", i0.ɵɵpureFunction2(4, _c0$c, ctx_r2.round.of, ctx_r2.round.of === (ctx_r2.nextRound == null ? null : ctx_r2.nextRound.of) && ctx_r2.round.bracket === (ctx_r2.nextRound == null ? null : ctx_r2.nextRound.bracket) ? "1" : "0")), " ");
} }
function MatchRoundOfComponent_ng_container_1_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵtemplate(1, MatchRoundOfComponent_ng_container_1_span_1_Template, 3, 7, "span", 2);
    i0.ɵɵelementContainerEnd();
} if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", ctx_r0.tournament.isDoubleElimination);
} }
const _c1$5 = function (a0) { return { of: a0 }; };
function MatchRoundOfComponent_ng_container_2_span_1_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "span");
    i0.ɵɵtext(1);
    i0.ɵɵpipe(2, "translate");
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r3 = i0.ɵɵnextContext(2);
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate1(" ", i0.ɵɵpipeBind2(2, 1, "path-view:round-of", i0.ɵɵpureFunction1(4, _c1$5, ctx_r3.round.of)), " ");
} }
function MatchRoundOfComponent_ng_container_2_span_2_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "span");
    i0.ɵɵtext(1);
    i0.ɵɵpipe(2, "translate");
    i0.ɵɵelementEnd();
} if (rf & 2) {
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate1(" ", i0.ɵɵpipeBind1(2, 1, "path-view:quarter-final"), " ");
} }
function MatchRoundOfComponent_ng_container_2_span_3_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "span");
    i0.ɵɵtext(1);
    i0.ɵɵpipe(2, "translate");
    i0.ɵɵelementEnd();
} if (rf & 2) {
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate1(" ", i0.ɵɵpipeBind1(2, 1, "path-view:semi-final"), " ");
} }
function MatchRoundOfComponent_ng_container_2_span_4_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "span");
    i0.ɵɵtext(1);
    i0.ɵɵpipe(2, "translate");
    i0.ɵɵelementEnd();
} if (rf & 2) {
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate1(" ", i0.ɵɵpipeBind1(2, 1, "path-view:upper-final"), " ");
} }
function MatchRoundOfComponent_ng_container_2_span_5_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "span");
    i0.ɵɵtext(1);
    i0.ɵɵpipe(2, "translate");
    i0.ɵɵelementEnd();
} if (rf & 2) {
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate1(" ", i0.ɵɵpipeBind1(2, 1, "path-view:third-place-match"), " ");
} }
function MatchRoundOfComponent_ng_container_2_span_6_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "span");
    i0.ɵɵtext(1);
    i0.ɵɵpipe(2, "translate");
    i0.ɵɵelementEnd();
} if (rf & 2) {
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate1(" ", i0.ɵɵpipeBind1(2, 1, "path-view:grand-final"), " ");
} }
function MatchRoundOfComponent_ng_container_2_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵtemplate(1, MatchRoundOfComponent_ng_container_2_span_1_Template, 3, 6, "span", 2);
    i0.ɵɵtemplate(2, MatchRoundOfComponent_ng_container_2_span_2_Template, 3, 3, "span", 2);
    i0.ɵɵtemplate(3, MatchRoundOfComponent_ng_container_2_span_3_Template, 3, 3, "span", 2);
    i0.ɵɵtemplate(4, MatchRoundOfComponent_ng_container_2_span_4_Template, 3, 3, "span", 2);
    i0.ɵɵtemplate(5, MatchRoundOfComponent_ng_container_2_span_5_Template, 3, 3, "span", 2);
    i0.ɵɵtemplate(6, MatchRoundOfComponent_ng_container_2_span_6_Template, 3, 3, "span", 2);
    i0.ɵɵelementContainerEnd();
} if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", ctx_r1.round.of > 8);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", ctx_r1.round.of == 8);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", ctx_r1.round.of == 4);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", ctx_r1.round.of == 2 && !ctx_r1.round.isFinal && ctx_r1.tournament.isDoubleElimination);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", ctx_r1.isThirdPlaceRound);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", ctx_r1.round.isFinal);
} }
const _c2$2 = function (a0) { return { "is-finished": a0 }; };
class MatchRoundOfComponent {
    constructor(roundService) {
        this.roundService = roundService;
    }
    ngOnChanges(changes) {
        this.isThirdPlaceRound = this.roundService.isThirdPlaceRound(this.round, this.tournament);
    }
}
MatchRoundOfComponent.ɵfac = function MatchRoundOfComponent_Factory(t) { return new (t || MatchRoundOfComponent)(i0.ɵɵdirectiveInject(RoundService)); };
MatchRoundOfComponent.ɵcmp = i0.ɵɵdefineComponent({ type: MatchRoundOfComponent, selectors: [["path-view-match-round-of"]], inputs: { round: "round", nextRound: "nextRound", match: "match", tournament: "tournament" }, features: [i0.ɵɵNgOnChangesFeature], decls: 3, vars: 6, consts: [[1, "quest-match__heading", 3, "ngSwitch", "ngClass"], [4, "ngSwitchCase"], [4, "ngIf"]], template: function MatchRoundOfComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵelementStart(0, "span", 0);
        i0.ɵɵtemplate(1, MatchRoundOfComponent_ng_container_1_Template, 2, 1, "ng-container", 1);
        i0.ɵɵtemplate(2, MatchRoundOfComponent_ng_container_2_Template, 7, 6, "ng-container", 1);
        i0.ɵɵelementEnd();
    } if (rf & 2) {
        i0.ɵɵproperty("ngSwitch", ctx.round.bracket == 0)("ngClass", i0.ɵɵpureFunction1(4, _c2$2, ctx.match.state === "closed"));
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngSwitchCase", false);
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngSwitchCase", true);
    } }, directives: [i1$1.NgSwitch, i1$1.NgClass, i1$1.NgSwitchCase, i1$1.NgIf], pipes: [i3.TranslatePipe], encapsulation: 2, changeDetection: 0 });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(MatchRoundOfComponent, [{
        type: Component,
        args: [{
                selector: "path-view-match-round-of",
                templateUrl: "./match-round-of.component.html",
                encapsulation: ViewEncapsulation.Emulated,
                changeDetection: ChangeDetectionStrategy.OnPush,
            }]
    }], function () { return [{ type: RoundService }]; }, { round: [{
            type: Input
        }], nextRound: [{
            type: Input
        }], match: [{
            type: Input
        }], tournament: [{
            type: Input
        }] }); })();

const _c0$b = ["*"];
class ContestantLinkComponent {
    constructor(linkService) {
        this.linkService = linkService;
    }
    ngOnChanges(changes) {
        if (isTeam(this.league)) {
            this.contestantUrl = this.linkService.teamContestant(this.league.uri, isRosterEnabled(this.league), +this.contestant.id);
        }
        else {
            this.contestantUrl = this.linkService.player(+this.contestant.id, this.contestant.alias);
        }
    }
}
ContestantLinkComponent.ɵfac = function ContestantLinkComponent_Factory(t) { return new (t || ContestantLinkComponent)(i0.ɵɵdirectiveInject(i1.LinkService)); };
ContestantLinkComponent.ɵcmp = i0.ɵɵdefineComponent({ type: ContestantLinkComponent, selectors: [["path-view-contestant-link"]], inputs: { league: "league", contestant: "contestant" }, features: [i0.ɵɵNgOnChangesFeature], ngContentSelectors: _c0$b, decls: 2, vars: 2, consts: [[3, "locationHref", "title"]], template: function ContestantLinkComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵprojectionDef();
        i0.ɵɵelementStart(0, "a", 0);
        i0.ɵɵprojection(1);
        i0.ɵɵelementEnd();
    } if (rf & 2) {
        i0.ɵɵproperty("locationHref", ctx.contestantUrl)("title", ctx.contestant.name);
    } }, directives: [i1.LocationDirective], encapsulation: 2, changeDetection: 0 });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(ContestantLinkComponent, [{
        type: Component,
        args: [{
                selector: "path-view-contestant-link",
                templateUrl: "./contestant-link.component.html",
                encapsulation: ViewEncapsulation.Emulated,
                changeDetection: ChangeDetectionStrategy.OnPush,
            }]
    }], function () { return [{ type: i1.LinkService }]; }, { league: [{
            type: Input
        }], contestant: [{
            type: Input
        }] }); })();

function MatchResultPlayerComponent_country_flag_1_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelement(0, "country-flag", 10);
} if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵproperty("country", ctx_r0.participants[ctx_r0.match.participants[ctx_r0.contestantIndex].id] == null ? null : ctx_r0.participants[ctx_r0.match.participants[ctx_r0.contestantIndex].id].region);
} }
function MatchResultPlayerComponent_span_3_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "span", 11);
    i0.ɵɵtext(1);
    i0.ɵɵpipe(2, "translate");
    i0.ɵɵelementEnd();
} if (rf & 2) {
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate1(" ", i0.ɵɵpipeBind1(2, 1, "path-view:tbd"), " ");
} }
function MatchResultPlayerComponent_ng_container_4_span_1_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "span", 11);
    i0.ɵɵtext(1);
    i0.ɵɵpipe(2, "translate");
    i0.ɵɵelementEnd();
} if (rf & 2) {
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate1(" ", i0.ɵɵpipeBind1(2, 1, "path-view:tbd"), " ");
} }
function MatchResultPlayerComponent_ng_container_4_span_2_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "span", 11);
    i0.ɵɵtext(1);
    i0.ɵɵpipe(2, "translate");
    i0.ɵɵelementEnd();
} if (rf & 2) {
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate1(" ", i0.ɵɵpipeBind1(2, 1, "path-view:defwin"), " ");
} }
function MatchResultPlayerComponent_ng_container_4_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵtemplate(1, MatchResultPlayerComponent_ng_container_4_span_1_Template, 3, 3, "span", 12);
    i0.ɵɵtemplate(2, MatchResultPlayerComponent_ng_container_4_span_2_Template, 3, 3, "span", 12);
    i0.ɵɵelementContainerEnd();
} if (rf & 2) {
    const ctx_r2 = i0.ɵɵnextContext();
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", ctx_r2.match.state === "open");
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", ctx_r2.match.state === "closed");
} }
function MatchResultPlayerComponent_span_5_path_view_contestant_link_1_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "path-view-contestant-link", 14);
    i0.ɵɵelement(1, "span", 15);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r11 = i0.ɵɵnextContext(2);
    i0.ɵɵproperty("contestant", ctx_r11.participants[ctx_r11.match.participants[ctx_r11.contestantIndex].id])("league", ctx_r11.league);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("innerHtml", ctx_r11.participants[ctx_r11.match.participants[ctx_r11.contestantIndex].id] == null ? null : ctx_r11.participants[ctx_r11.match.participants[ctx_r11.contestantIndex].id].name, i0.ɵɵsanitizeHtml);
} }
function MatchResultPlayerComponent_span_5_span_2_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "span");
    i0.ɵɵtext(1);
    i0.ɵɵpipe(2, "translate");
    i0.ɵɵelementEnd();
} if (rf & 2) {
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate1(" ", i0.ɵɵpipeBind1(2, 1, "path-view:tbd"), " ");
} }
function MatchResultPlayerComponent_span_5_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "span", 11);
    i0.ɵɵtemplate(1, MatchResultPlayerComponent_span_5_path_view_contestant_link_1_Template, 2, 3, "path-view-contestant-link", 13);
    i0.ɵɵtemplate(2, MatchResultPlayerComponent_span_5_span_2_Template, 3, 3, "span", 6);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r3 = i0.ɵɵnextContext();
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", ctx_r3.participants[ctx_r3.match.participants[ctx_r3.contestantIndex].id]);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", !ctx_r3.match.participants[ctx_r3.contestantIndex].id);
} }
function MatchResultPlayerComponent_span_6_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "span", 16);
    i0.ɵɵelementStart(1, "path-view-contestant-link", 14);
    i0.ɵɵelement(2, "span", 15);
    i0.ɵɵelementEnd();
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r4 = i0.ɵɵnextContext();
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("contestant", ctx_r4.participants[ctx_r4.match.participants[ctx_r4.contestantIndex].id])("league", ctx_r4.league);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("innerHtml", ctx_r4.participants[ctx_r4.match.participants[ctx_r4.contestantIndex].id] == null ? null : ctx_r4.participants[ctx_r4.match.participants[ctx_r4.contestantIndex].id].name, i0.ɵɵsanitizeHtml);
} }
function MatchResultPlayerComponent_premium_badge_7_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelement(0, "premium-badge");
} }
function MatchResultPlayerComponent_button_8_Template(rf, ctx) { if (rf & 1) {
    const _r14 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "button", 17);
    i0.ɵɵlistener("click", function MatchResultPlayerComponent_button_8_Template_button_click_0_listener() { i0.ɵɵrestoreView(_r14); const ctx_r13 = i0.ɵɵnextContext(); return ctx_r13.switchPov(ctx_r13.match.participants[ctx_r13.contestantIndex].id); });
    i0.ɵɵelement(1, "svg-icon", 18);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("icon", "eye");
} }
function MatchResultPlayerComponent_span_10_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "span");
    i0.ɵɵtext(1, "-");
    i0.ɵɵelementEnd();
} }
function MatchResultPlayerComponent_span_11_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "span");
    i0.ɵɵtext(1);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r8 = i0.ɵɵnextContext();
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate1(" ", ctx_r8.bestOfScore, " ");
} }
const _c0$a = function (a0, a1) { return { "is-win": a0, "is-loss": a1 }; };
const _c1$4 = function (a0) { return { "is-future": a0 }; };
class MatchResultPlayerComponent {
    constructor() {
        this.selected = new EventEmitter();
    }
    switchPov(id) {
        this.selected.emit(id);
    }
}
MatchResultPlayerComponent.ɵfac = function MatchResultPlayerComponent_Factory(t) { return new (t || MatchResultPlayerComponent)(); };
MatchResultPlayerComponent.ɵcmp = i0.ɵɵdefineComponent({ type: MatchResultPlayerComponent, selectors: [["path-view-match-result-player"]], inputs: { round: "round", participants: "participants", participantsDetails: "participantsDetails", match: "match", contestantIndex: "contestantIndex", isFuture: "isFuture", selectedParticipant: "selectedParticipant", bestOfScore: "bestOfScore", league: "league" }, outputs: { selected: "selected" }, decls: 12, vars: 16, consts: [[1, "quest-match__result__player", 3, "ngClass"], ["class", "quest-match__result__player__flag", 3, "country", 4, "ngIf"], [1, "quest-match__result__player__name", 3, "ngClass", "ngSwitch"], ["class", "contestant-name contestant-name--long", 4, "ngSwitchCase"], [4, "ngSwitchCase"], ["class", "quest-match__result__player__name-wrapper", 4, "ngSwitchDefault"], [4, "ngIf"], ["class", "contestant__pov-switch", 3, "click", 4, "ngIf"], [1, "quest-match__result__player__score", 3, "ngSwitch"], [4, "ngSwitchDefault"], [1, "quest-match__result__player__flag", 3, "country"], [1, "contestant-name", "contestant-name--long"], ["class", "contestant-name contestant-name--long", 4, "ngIf"], [3, "contestant", "league", 4, "ngIf"], [3, "contestant", "league"], [3, "innerHtml"], [1, "quest-match__result__player__name-wrapper"], [1, "contestant__pov-switch", 3, "click"], [1, "contestant__eye", 3, "icon"]], template: function MatchResultPlayerComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵelementStart(0, "div", 0);
        i0.ɵɵtemplate(1, MatchResultPlayerComponent_country_flag_1_Template, 1, 1, "country-flag", 1);
        i0.ɵɵelementStart(2, "span", 2);
        i0.ɵɵtemplate(3, MatchResultPlayerComponent_span_3_Template, 3, 3, "span", 3);
        i0.ɵɵtemplate(4, MatchResultPlayerComponent_ng_container_4_Template, 3, 2, "ng-container", 4);
        i0.ɵɵtemplate(5, MatchResultPlayerComponent_span_5_Template, 3, 2, "span", 3);
        i0.ɵɵtemplate(6, MatchResultPlayerComponent_span_6_Template, 3, 3, "span", 5);
        i0.ɵɵtemplate(7, MatchResultPlayerComponent_premium_badge_7_Template, 1, 0, "premium-badge", 6);
        i0.ɵɵelementEnd();
        i0.ɵɵtemplate(8, MatchResultPlayerComponent_button_8_Template, 2, 1, "button", 7);
        i0.ɵɵelementStart(9, "span", 8);
        i0.ɵɵtemplate(10, MatchResultPlayerComponent_span_10_Template, 2, 0, "span", 4);
        i0.ɵɵtemplate(11, MatchResultPlayerComponent_span_11_Template, 2, 1, "span", 9);
        i0.ɵɵelementEnd();
        i0.ɵɵelementEnd();
    } if (rf & 2) {
        i0.ɵɵproperty("ngClass", i0.ɵɵpureFunction2(11, _c0$a, ctx.match.participants[ctx.contestantIndex].place === 1, ctx.match.participants[ctx.contestantIndex].place > 1));
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngIf", ctx.match.participants[ctx.contestantIndex].id);
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngClass", i0.ɵɵpureFunction1(14, _c1$4, ctx.isFuture))("ngSwitch", ctx.match.participants[ctx.contestantIndex].id);
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngSwitchCase", undefined);
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngSwitchCase", 0);
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngSwitchCase", true);
        i0.ɵɵadvance(2);
        i0.ɵɵproperty("ngIf", ctx.participantsDetails[ctx.match.participants[ctx.contestantIndex].id] == null ? null : ctx.participantsDetails[ctx.match.participants[ctx.contestantIndex].id].premium);
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngIf", ctx.match.participants[ctx.contestantIndex].id && ctx.selectedParticipant !== ctx.match.participants[ctx.contestantIndex].id);
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngSwitch", ctx.match.participants[ctx.contestantIndex] == null ? null : ctx.match.participants[ctx.contestantIndex].points == null ? null : ctx.match.participants[ctx.contestantIndex].points.length);
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngSwitchCase", undefined);
    } }, directives: [i1$1.NgClass, i1$1.NgIf, i1$1.NgSwitch, i1$1.NgSwitchCase, i1$1.NgSwitchDefault, i1.CountryFlagComponent, ContestantLinkComponent, i4.PremiumBadgeComponent, i1.SvgIconComponent], pipes: [i3.TranslatePipe], encapsulation: 2, changeDetection: 0 });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(MatchResultPlayerComponent, [{
        type: Component,
        args: [{
                selector: "path-view-match-result-player",
                templateUrl: "./match-result-player.component.html",
                encapsulation: ViewEncapsulation.Emulated,
                changeDetection: ChangeDetectionStrategy.OnPush,
            }]
    }], null, { selected: [{
            type: Output
        }], round: [{
            type: Input
        }], participants: [{
            type: Input
        }], participantsDetails: [{
            type: Input
        }], match: [{
            type: Input
        }], contestantIndex: [{
            type: Input
        }], isFuture: [{
            type: Input
        }], selectedParticipant: [{
            type: Input
        }], bestOfScore: [{
            type: Input
        }], league: [{
            type: Input
        }] }); })();

class MatchComponent {
    constructor(store, matchService) {
        this.store = store;
        this.matchService = matchService;
    }
    /**
     * @param score
     */
    getBestOfScore(score) {
        if (isUndefined(score)) {
            return;
        }
        return reduce(score, function (sum, n) {
            return sum + n;
        }, 0).toString();
    }
    /**
     * Get the index of the current contestant in the match
     */
    getContestantIndex() {
        return this.matchService.getContestantIndexOfMatch(this.match, this.round.contestantId);
    }
    /**
     * Get the index of the other contestant in the match
     */
    getOtherContestantIndex() {
        const contestantIndex = this.getContestantIndex();
        return contestantIndex ? 0 : 1;
    }
    /**
     * Switch pov to a specific contestant.
     */
    selectParticipant(id) {
        this.store.dispatch(new SelectParticipantAction(this.league.id, id));
    }
}
MatchComponent.ɵfac = function MatchComponent_Factory(t) { return new (t || MatchComponent)(i0.ɵɵdirectiveInject(i1$2.Store), i0.ɵɵdirectiveInject(MatchService)); };
MatchComponent.ɵcmp = i0.ɵɵdefineComponent({ type: MatchComponent, selectors: [["path-view-match"]], inputs: { tournament: "tournament", league: "league", selectedParticipant: "selectedParticipant", round: "round", match: "match", participants: "participants", participantsDetails: "participantsDetails", state: "state", nextRound: "nextRound" }, decls: 8, vars: 24, consts: [[1, "quest-match"], [3, "round", "nextRound", "match", "tournament"], [1, "quest-match__result"], [1, "quest-match__result__wrapper", 3, "match", "contestantIndex", "isFuture", "selectedParticipant", "round", "participants", "participantsDetails", "bestOfScore", "league", "selected"], [3, "matchId", "leagueUri"], [1, "quest-match__result__vs"]], template: function MatchComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵelementStart(0, "article", 0);
        i0.ɵɵelement(1, "path-view-match-round-of", 1);
        i0.ɵɵelementStart(2, "div", 2);
        i0.ɵɵelementStart(3, "path-view-match-result-player", 3);
        i0.ɵɵlistener("selected", function MatchComponent_Template_path_view_match_result_player_selected_3_listener($event) { return ctx.selectParticipant($event); });
        i0.ɵɵelementEnd();
        i0.ɵɵelementStart(4, "path-view-match-result-player", 3);
        i0.ɵɵlistener("selected", function MatchComponent_Template_path_view_match_result_player_selected_4_listener($event) { return ctx.selectParticipant($event); });
        i0.ɵɵelementEnd();
        i0.ɵɵelementStart(5, "path-view-match-link", 4);
        i0.ɵɵelementStart(6, "span", 5);
        i0.ɵɵtext(7, "VS");
        i0.ɵɵelementEnd();
        i0.ɵɵelementEnd();
        i0.ɵɵelementEnd();
        i0.ɵɵelementEnd();
    } if (rf & 2) {
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("round", ctx.round)("nextRound", ctx.nextRound)("match", ctx.match)("tournament", ctx.tournament);
        i0.ɵɵadvance(2);
        i0.ɵɵproperty("match", ctx.match)("contestantIndex", ctx.getContestantIndex())("isFuture", ctx.state.isFuture())("selectedParticipant", ctx.selectedParticipant)("round", ctx.round)("participants", ctx.participants)("participantsDetails", ctx.participantsDetails)("bestOfScore", ctx.getBestOfScore(ctx.match.participants[ctx.getContestantIndex()].points))("league", ctx.league);
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("match", ctx.match)("contestantIndex", ctx.getOtherContestantIndex())("isFuture", ctx.state.isFuture())("selectedParticipant", ctx.selectedParticipant)("round", ctx.round)("participants", ctx.participants)("participantsDetails", ctx.participantsDetails)("bestOfScore", ctx.getBestOfScore(ctx.match.participants[ctx.getOtherContestantIndex()].points))("league", ctx.league);
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("matchId", ctx.match.id)("leagueUri", ctx.league.uri);
    } }, directives: [MatchRoundOfComponent, MatchResultPlayerComponent, MatchLinkComponent], encapsulation: 2, changeDetection: 0 });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(MatchComponent, [{
        type: Component,
        args: [{
                selector: "path-view-match",
                templateUrl: "./match.component.html",
                styles: [],
                encapsulation: ViewEncapsulation.Emulated,
                changeDetection: ChangeDetectionStrategy.OnPush,
            }]
    }], function () { return [{ type: i1$2.Store }, { type: MatchService }]; }, { tournament: [{
            type: Input
        }], league: [{
            type: Input
        }], selectedParticipant: [{
            type: Input
        }], round: [{
            type: Input
        }], match: [{
            type: Input
        }], participants: [{
            type: Input
        }], participantsDetails: [{
            type: Input
        }], state: [{
            type: Input
        }], nextRound: [{
            type: Input
        }] }); })();

const _c0$9 = function (a0, a1) { return { "is-win": a0, "is-loss": a1 }; };
const _c1$3 = ["*"];
class FinalRoundComponent {
    constructor() { }
}
FinalRoundComponent.ɵfac = function FinalRoundComponent_Factory(t) { return new (t || FinalRoundComponent)(); };
FinalRoundComponent.ɵcmp = i0.ɵɵdefineComponent({ type: FinalRoundComponent, selectors: [["path-view-final-round"]], inputs: { wonTournament: "wonTournament" }, ngContentSelectors: _c1$3, decls: 5, vars: 9, consts: [[1, "round-message__banner", "is-eliminated", 3, "ngClass"], [1, "round-message__banner__badge", 3, "ngClass"], [1, "round-message__banner__badge__inner"], [1, "round-message__banner__badge__icon", 3, "icon"]], template: function FinalRoundComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵprojectionDef();
        i0.ɵɵelementStart(0, "section", 0);
        i0.ɵɵelementStart(1, "div", 1);
        i0.ɵɵelement(2, "div", 2);
        i0.ɵɵelement(3, "svg-icon", 3);
        i0.ɵɵelementEnd();
        i0.ɵɵprojection(4);
        i0.ɵɵelementEnd();
    } if (rf & 2) {
        i0.ɵɵproperty("ngClass", i0.ɵɵpureFunction2(3, _c0$9, ctx.wonTournament, !ctx.wonTournament));
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngClass", i0.ɵɵpureFunction2(6, _c0$9, ctx.wonTournament, !ctx.wonTournament));
        i0.ɵɵadvance(2);
        i0.ɵɵproperty("icon", "star");
    } }, directives: [i1$1.NgClass, i1.SvgIconComponent], encapsulation: 2, changeDetection: 0 });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(FinalRoundComponent, [{
        type: Component,
        args: [{
                selector: "path-view-final-round",
                templateUrl: "./final-round.component.html",
                styles: [],
                encapsulation: ViewEncapsulation.Emulated,
                changeDetection: ChangeDetectionStrategy.OnPush,
            }]
    }], function () { return []; }, { wonTournament: [{
            type: Input
        }] }); })();

const _c0$8 = ["*"];
class DroppedDownComponent {
    constructor() { }
}
DroppedDownComponent.ɵfac = function DroppedDownComponent_Factory(t) { return new (t || DroppedDownComponent)(); };
DroppedDownComponent.ɵcmp = i0.ɵɵdefineComponent({ type: DroppedDownComponent, selectors: [["path-view-dropped-down"]], ngContentSelectors: _c0$8, decls: 2, vars: 0, consts: [[1, "round-message__banner", "is-alive"]], template: function DroppedDownComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵprojectionDef();
        i0.ɵɵelementStart(0, "section", 0);
        i0.ɵɵprojection(1);
        i0.ɵɵelementEnd();
    } }, encapsulation: 2, changeDetection: 0 });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DroppedDownComponent, [{
        type: Component,
        args: [{
                selector: "path-view-dropped-down",
                templateUrl: "./dropped-down.component.html",
                styles: [],
                encapsulation: ViewEncapsulation.Emulated,
                changeDetection: ChangeDetectionStrategy.OnPush,
            }]
    }], function () { return []; }, null); })();

const _c0$7 = ["*"];
class DroppedOutComponent {
    constructor() { }
}
DroppedOutComponent.ɵfac = function DroppedOutComponent_Factory(t) { return new (t || DroppedOutComponent)(); };
DroppedOutComponent.ɵcmp = i0.ɵɵdefineComponent({ type: DroppedOutComponent, selectors: [["path-view-dropped-out"]], ngContentSelectors: _c0$7, decls: 5, vars: 1, consts: [[1, "round-message__banner", "is-eliminated"], [1, "round-message__banner__badge"], [1, "round-message__banner__badge__inner"], [1, "round-message__banner__badge__icon", 3, "icon"]], template: function DroppedOutComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵprojectionDef();
        i0.ɵɵelementStart(0, "section", 0);
        i0.ɵɵelementStart(1, "div", 1);
        i0.ɵɵelement(2, "div", 2);
        i0.ɵɵelement(3, "svg-icon", 3);
        i0.ɵɵelementEnd();
        i0.ɵɵprojection(4);
        i0.ɵɵelementEnd();
    } if (rf & 2) {
        i0.ɵɵadvance(3);
        i0.ɵɵproperty("icon", "star");
    } }, directives: [i1.SvgIconComponent], encapsulation: 2, changeDetection: 0 });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DroppedOutComponent, [{
        type: Component,
        args: [{
                selector: "path-view-dropped-out",
                templateUrl: "./dropped-out.component.html",
                styles: [],
                encapsulation: ViewEncapsulation.Emulated,
                changeDetection: ChangeDetectionStrategy.OnPush,
            }]
    }], function () { return []; }, null); })();

class FutureMatchesCancelledComponent {
    constructor() { }
}
FutureMatchesCancelledComponent.ɵfac = function FutureMatchesCancelledComponent_Factory(t) { return new (t || FutureMatchesCancelledComponent)(); };
FutureMatchesCancelledComponent.ɵcmp = i0.ɵɵdefineComponent({ type: FutureMatchesCancelledComponent, selectors: [["path-view-future-matches-cancelled"]], decls: 4, vars: 3, consts: [[1, "round-message__all-cancelled"]], template: function FutureMatchesCancelledComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵelementStart(0, "section", 0);
        i0.ɵɵelementStart(1, "span");
        i0.ɵɵtext(2);
        i0.ɵɵpipe(3, "translate");
        i0.ɵɵelementEnd();
        i0.ɵɵelementEnd();
    } if (rf & 2) {
        i0.ɵɵadvance(2);
        i0.ɵɵtextInterpolate(i0.ɵɵpipeBind1(3, 1, "path-view:message-all-cancelled"));
    } }, pipes: [i3.TranslatePipe], encapsulation: 2, changeDetection: 0 });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(FutureMatchesCancelledComponent, [{
        type: Component,
        args: [{
                selector: "path-view-future-matches-cancelled",
                templateUrl: "./future-matches-cancelled.component.html",
                styles: [],
                encapsulation: ViewEncapsulation.Emulated,
                changeDetection: ChangeDetectionStrategy.OnPush,
            }]
    }], function () { return []; }, null); })();

function NextRoundComponent_span_1_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "span");
    i0.ɵɵtext(1);
    i0.ɵɵpipe(2, "translate");
    i0.ɵɵelementEnd();
} if (rf & 2) {
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate(i0.ɵɵpipeBind1(2, 1, "path-view:message-cancelled"));
} }
function NextRoundComponent_path_view_contestant_link_5_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "path-view-contestant-link", 3);
    i0.ɵɵtext(1);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵproperty("contestant", ctx_r1.contestant)("league", ctx_r1.league);
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate1(" ", ctx_r1.contestant.name, " ");
} }
class NextRoundComponent {
    constructor() { }
}
NextRoundComponent.ɵfac = function NextRoundComponent_Factory(t) { return new (t || NextRoundComponent)(); };
NextRoundComponent.ɵcmp = i0.ɵɵdefineComponent({ type: NextRoundComponent, selectors: [["path-view-next-round"]], inputs: { contestant: "contestant", league: "league", cancelled: "cancelled" }, decls: 6, vars: 5, consts: [[1, "round-message__following"], [4, "ngIf"], [3, "contestant", "league", 4, "ngIf"], [3, "contestant", "league"]], template: function NextRoundComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵelementStart(0, "section", 0);
        i0.ɵɵtemplate(1, NextRoundComponent_span_1_Template, 3, 3, "span", 1);
        i0.ɵɵelementStart(2, "span");
        i0.ɵɵtext(3);
        i0.ɵɵpipe(4, "translate");
        i0.ɵɵelementEnd();
        i0.ɵɵtemplate(5, NextRoundComponent_path_view_contestant_link_5_Template, 2, 3, "path-view-contestant-link", 2);
        i0.ɵɵelementEnd();
    } if (rf & 2) {
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngIf", ctx.cancelled);
        i0.ɵɵadvance(2);
        i0.ɵɵtextInterpolate1("", i0.ɵɵpipeBind1(4, 3, "path-view:message-now-following"), " ");
        i0.ɵɵadvance(2);
        i0.ɵɵproperty("ngIf", ctx.contestant);
    } }, directives: [i1$1.NgIf, ContestantLinkComponent], pipes: [i3.TranslatePipe], styles: ["path-view-dropped-out    + [_nghost-%COMP%] {\n                margin-top: 2em;\n            }"], changeDetection: 0 });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(NextRoundComponent, [{
        type: Component,
        args: [{
                selector: "path-view-next-round",
                templateUrl: "./next-round.component.html",
                styles: [
                    `
            path-view-dropped-out + :host {
                margin-top: 2em;
            }
        `,
                ],
                encapsulation: ViewEncapsulation.Emulated,
                changeDetection: ChangeDetectionStrategy.OnPush,
            }]
    }], function () { return []; }, { contestant: [{
            type: Input
        }], league: [{
            type: Input
        }], cancelled: [{
            type: Input
        }] }); })();

class NextRoundOnHoldComponent {
    constructor() { }
}
NextRoundOnHoldComponent.ɵfac = function NextRoundOnHoldComponent_Factory(t) { return new (t || NextRoundOnHoldComponent)(); };
NextRoundOnHoldComponent.ɵcmp = i0.ɵɵdefineComponent({ type: NextRoundOnHoldComponent, selectors: [["path-view-next-round-on-hold"]], decls: 4, vars: 3, consts: [[1, "round-message__following"]], template: function NextRoundOnHoldComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵelementStart(0, "section", 0);
        i0.ɵɵelementStart(1, "span");
        i0.ɵɵtext(2);
        i0.ɵɵpipe(3, "translate");
        i0.ɵɵelementEnd();
        i0.ɵɵelementEnd();
    } if (rf & 2) {
        i0.ɵɵadvance(2);
        i0.ɵɵtextInterpolate1("", i0.ɵɵpipeBind1(3, 1, "path-view:waiting-to-follow"), " ");
    } }, pipes: [i3.TranslatePipe], encapsulation: 2, changeDetection: 0 });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(NextRoundOnHoldComponent, [{
        type: Component,
        args: [{
                selector: "path-view-next-round-on-hold",
                templateUrl: "./next-round-on-hold.component.html",
                styles: [],
                encapsulation: ViewEncapsulation.Emulated,
                changeDetection: ChangeDetectionStrategy.OnPush,
            }]
    }], function () { return []; }, null); })();

function RoundMessageComponent_div_1_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelement(0, "div", 6);
} }
const _c0$6 = function (a0, a1, a2, a3) { return { isContestant: a0, contestant: a1, count: a2, type: a3 }; };
function RoundMessageComponent_path_view_final_round_2_span_1_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "span");
    i0.ɵɵtext(1);
    i0.ɵɵpipe(2, "translate");
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r9 = i0.ɵɵnextContext(2);
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate1(" ", i0.ɵɵpipeBind2(2, 1, "path-view:message-final-won", i0.ɵɵpureFunction4(4, _c0$6, ctx_r9.isViewer ? 1 : 0, ctx_r9.participants[ctx_r9.round.contestantId] == null ? null : ctx_r9.participants[ctx_r9.round.contestantId].name, ctx_r9.participantCount - 1, ctx_r9.tournament.isTeam ? "team" : "player")), " ");
} }
const _c1$2 = function (a0, a1, a2, a3, a4) { return { isContestant: a0, contestant: a1, count: a2, percentage: a3, type: a4 }; };
function RoundMessageComponent_path_view_final_round_2_span_2_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "span");
    i0.ɵɵtext(1);
    i0.ɵɵpipe(2, "translate");
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r10 = i0.ɵɵnextContext(2);
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate1(" ", i0.ɵɵpipeBind2(2, 1, "path-view:message-final-lost", i0.ɵɵpureFunction5(4, _c1$2, ctx_r10.isViewer ? 1 : 0, ctx_r10.participants[ctx_r10.round.contestantId] == null ? null : ctx_r10.participants[ctx_r10.round.contestantId].name, ctx_r10.participantCount - 2, 100 - ctx_r10.percentage, ctx_r10.tournament.isTeam ? "team" : "player")), " ");
} }
function RoundMessageComponent_path_view_final_round_2_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "path-view-final-round", 7);
    i0.ɵɵtemplate(1, RoundMessageComponent_path_view_final_round_2_span_1_Template, 3, 9, "span", 8);
    i0.ɵɵtemplate(2, RoundMessageComponent_path_view_final_round_2_span_2_Template, 3, 10, "span", 8);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵproperty("wonTournament", ctx_r1.state.isWin())("ngSwitch", ctx_r1.state.isWin());
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngSwitchCase", true);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngSwitchCase", false);
} }
const _c2$1 = function (a0, a1, a2, a3) { return { isContestant: a0, contestant: a1, of: a2, type: a3 }; };
function RoundMessageComponent_path_view_dropped_down_3_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "path-view-dropped-down");
    i0.ɵɵtext(1);
    i0.ɵɵpipe(2, "translate");
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r2 = i0.ɵɵnextContext();
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate1(" ", i0.ɵɵpipeBind2(2, 1, "path-view:message-lost", i0.ɵɵpureFunction4(4, _c2$1, ctx_r2.isViewer ? 1 : 0, ctx_r2.participants[ctx_r2.round.contestantId] == null ? null : ctx_r2.participants[ctx_r2.round.contestantId].name, ctx_r2.round.of, ctx_r2.tournament.isTeam ? "team" : "player")), " ");
} }
function RoundMessageComponent_path_view_dropped_down_4_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "path-view-dropped-down");
    i0.ɵɵtext(1);
    i0.ɵɵpipe(2, "translate");
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r3 = i0.ɵɵnextContext();
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate1(" ", i0.ɵɵpipeBind2(2, 1, "path-view:message-lost-third", i0.ɵɵpureFunction4(4, _c2$1, ctx_r3.isViewer ? 1 : 0, ctx_r3.participants[ctx_r3.round.contestantId] == null ? null : ctx_r3.participants[ctx_r3.round.contestantId].name, ctx_r3.round.of, ctx_r3.tournament.isTeam ? "team" : "player")), " ");
} }
const _c3$1 = function (a0, a1, a2, a3) { return { isContestant: a0, contestant: a1, place: a2, type: a3 }; };
function RoundMessageComponent_path_view_dropped_out_5_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "path-view-dropped-out");
    i0.ɵɵtext(1);
    i0.ɵɵpipe(2, "translate");
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r4 = i0.ɵɵnextContext();
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate1(" ", i0.ɵɵpipeBind2(2, 1, "path-view:message-thirdplace", i0.ɵɵpureFunction4(4, _c3$1, ctx_r4.isViewer ? 1 : 0, ctx_r4.participants[ctx_r4.round.contestantId] == null ? null : ctx_r4.participants[ctx_r4.round.contestantId].name, ctx_r4.state.isWin() ? 3 : 4, ctx_r4.tournament.isTeam ? "team" : "player")), " ");
} }
const _c4 = function (a0, a1, a2, a3, a4, a5) { return { isContestant: a0, contestant: a1, count: a2, player: a3, team: a4, type: a5 }; };
function RoundMessageComponent_path_view_dropped_out_6_span_1_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "span");
    i0.ɵɵtext(1);
    i0.ɵɵpipe(2, "translate");
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r11 = i0.ɵɵnextContext(2);
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate1(" ", i0.ɵɵpipeBind2(2, 1, "path-view:message-eliminated-round1", i0.ɵɵpureFunction6(4, _c4, ctx_r11.isViewer ? 1 : 0, ctx_r11.participants[ctx_r11.round.contestantId] == null ? null : ctx_r11.participants[ctx_r11.round.contestantId].name, ctx_r11.round.droppedOutContestants.length - 1, !ctx_r11.tournament.isTeam, ctx_r11.tournament.isTeam, ctx_r11.tournament.isTeam ? "team" : "player")), " ");
} }
const _c5 = function (a0, a1, a2, a3, a4, a5, a6, a7, a8, a9) { return { isContestant: a0, contestant: a1, of: a2, bracket: a3, count: a4, percentage: a5, player: a6, team: a7, type: a8, first: a9 }; };
function RoundMessageComponent_path_view_dropped_out_6_span_2_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "span");
    i0.ɵɵtext(1);
    i0.ɵɵpipe(2, "translate");
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r12 = i0.ɵɵnextContext(2);
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate1(" ", i0.ɵɵpipeBind2(2, 1, "path-view:message-eliminated", i0.ɵɵpureFunctionV(4, _c5, [ctx_r12.isViewer ? 1 : 0, ctx_r12.participants[ctx_r12.round.contestantId] == null ? null : ctx_r12.participants[ctx_r12.round.contestantId].name, ctx_r12.round.of, ctx_r12.round.bracket, ctx_r12.roundStats.count, ctx_r12.roundStats.percentage >= 1 ? ctx_r12.roundStats.percentage : 1, !ctx_r12.tournament.isTeam, ctx_r12.tournament.isTeam, ctx_r12.tournament.isTeam ? "team" : "player", ctx_r12.round.of === ctx_r12.nextRound.of && ctx_r12.round.bracket === ctx_r12.nextRound.bracket ? "1" : "0"])), " ");
} }
function RoundMessageComponent_path_view_dropped_out_6_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "path-view-dropped-out", 9);
    i0.ɵɵtemplate(1, RoundMessageComponent_path_view_dropped_out_6_span_1_Template, 3, 11, "span", 8);
    i0.ɵɵtemplate(2, RoundMessageComponent_path_view_dropped_out_6_span_2_Template, 3, 15, "span", 8);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r5 = i0.ɵɵnextContext();
    i0.ɵɵproperty("ngSwitch", ctx_r5.isFirstEliminationRound);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngSwitchCase", true);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngSwitchCase", false);
} }
function RoundMessageComponent_path_view_future_matches_cancelled_7_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelement(0, "path-view-future-matches-cancelled");
} }
function RoundMessageComponent_path_view_next_round_8_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelement(0, "path-view-next-round", 10);
} if (rf & 2) {
    const ctx_r7 = i0.ɵɵnextContext();
    i0.ɵɵproperty("league", ctx_r7.league)("contestant", ctx_r7.participants[ctx_r7.following.id])("cancelled", ctx_r7.state.isCancelled());
} }
function RoundMessageComponent_path_view_next_round_on_hold_9_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelement(0, "path-view-next-round-on-hold");
} }
const _c6 = function (a0, a1) { return { "round-message--big": a0, "round-message--middle": a1 }; };
class RoundMessageComponent {
    constructor(roundService, doubleElimination) {
        this.roundService = roundService;
        this.doubleElimination = doubleElimination;
    }
    ngOnChanges() {
        this.roundStats = this.roundService.getRoundStats(this.matches, this.round);
        this.participantCount = this.getParticipantCount();
        this.percentage = this.getPercentage();
        this.isViewer = this.getIsViewer();
        this.isPast = this.getIsPast();
        this.isPastFinal = this.getIsPastFinal();
        this.isKnockedDown = this.getIsKnockedDown();
        this.isKnockedDownToThird = this.getIsKnockedDownToThird();
        this.isEliminated = this.getIsEliminated();
        this.isFollowingOnHold = this.getIsFollowingOnHold();
        this.allFutureMatchesCanceled = this.areAllFutureMatchesCanceled();
        this.isFirstEliminationRound = this.getIsFirstEliminationRound();
        this.isThirdPlaceRound = this.roundService.isThirdPlaceRound(this.round, this.tournament);
    }
    getParticipantCount() {
        return Object.keys(this.participants).length;
    }
    getPercentage() {
        return Math.max(0, Math.floor(100 - (100 / this.participantCount) * this.round.of));
    }
    getIsViewer() {
        return this.ownContestant && this.ownContestant === this.round.contestantId;
    }
    // Message conditions
    getIsPast() {
        return this.state.isLoss() || this.state.isWin();
    }
    /**
     * Checks if the round is the last match and has been played
     */
    getIsPastFinal() {
        let isFinal = this.state.isFinal();
        if (this.doubleElimination.isDoubleEliminationCupMode(this.matches)) {
            /**
             * If the first grand final is the last round (when upper bracket player wins first grandfinal)
             * then the first grand final is also the last
             */
            if (!this.nextRound) {
                isFinal = this.doubleElimination.isGrandFinal(this.match, this.matches);
            }
            else {
                isFinal = this.match === this.doubleElimination.getLastGrandFinal(this.matches);
            }
        }
        return isFinal && this.isPast && this.isSelf;
    }
    /**
     * Checks if round knocked the contestant down into the lower bracket
     */
    getIsKnockedDown() {
        return (!this.state.isEliminated() &&
            !this.state.isFinal() &&
            this.doubleElimination.isDoubleEliminationCupMode(this.matches) &&
            this.state.isLoss() &&
            this.isSelf);
    }
    /**
     * Checks if round knocked the contestant down into the third match
     */
    getIsKnockedDownToThird() {
        return (!this.state.isEliminated() &&
            !this.state.isFinal() &&
            this.state.isLoss() &&
            !this.doubleElimination.isDoubleEliminationCupMode(this.matches) &&
            this.isSelf);
    }
    /**
     * Checks if the contestant has been knocked out of the tournmanent
     */
    getIsEliminated() {
        return (this.state.isEliminated() &&
            !this.roundService.isThirdPlaceRound(this.round, this.tournament) &&
            !this.state.isFinal() &&
            this.isSelf);
    }
    /**
     * Checks if theres noone to follow
     * Can happen when matches are deleted and part of the bracket hasnt played yet
     */
    getIsFollowingOnHold() {
        return this.round.contestantId && this.nextRound && this.nextRound.contestantId === 0;
    }
    /**
     * Checks if theres never gonna be a match to follow anymore
     */
    areAllFutureMatchesCanceled() {
        /**
         * The last match is gonna be canceled in a double double elimination cup if
         * the upper bracket winner wins the first match, thus we do not want to show the cancelled message
         */
        if (this.doubleElimination.isDoubleEliminationCupMode(this.matches)) {
            if (!this.nextRound) {
                return !this.doubleElimination.isGrandFinal(this.match, this.matches);
            }
        }
        return !this.round.isFinal && !this.nextRound;
    }
    /**
     * Checks if round is the first elimination round in the cup.
     */
    getIsFirstEliminationRound() {
        const isDoubleElimination = this.doubleElimination.isDoubleEliminationCupMode(this.matches);
        const firstRound = this.matches.length ? first(this.matches).round : 0;
        const firstEliminationRound = isDoubleElimination ? firstRound + 1 : firstRound;
        return this.round.round === firstEliminationRound;
    }
}
RoundMessageComponent.ɵfac = function RoundMessageComponent_Factory(t) { return new (t || RoundMessageComponent)(i0.ɵɵdirectiveInject(RoundService), i0.ɵɵdirectiveInject(DoubleEliminationService)); };
RoundMessageComponent.ɵcmp = i0.ɵɵdefineComponent({ type: RoundMessageComponent, selectors: [["path-view-round-message"]], inputs: { tournament: "tournament", ownContestant: "ownContestant", round: "round", nextRound: "nextRound", following: "following", participants: "participants", match: "match", matches: "matches", state: "state", isSelf: "isSelf", league: "league" }, features: [i0.ɵɵNgOnChangesFeature], decls: 10, vars: 13, consts: [[1, "round-message", 3, "ngClass"], ["class", "round-message__line", 4, "ngIf"], [3, "wonTournament", "ngSwitch", 4, "ngIf"], [4, "ngIf"], [3, "ngSwitch", 4, "ngIf"], [3, "league", "contestant", "cancelled", 4, "ngIf"], [1, "round-message__line"], [3, "wonTournament", "ngSwitch"], [4, "ngSwitchCase"], [3, "ngSwitch"], [3, "league", "contestant", "cancelled"]], template: function RoundMessageComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵelementStart(0, "article", 0);
        i0.ɵɵtemplate(1, RoundMessageComponent_div_1_Template, 1, 0, "div", 1);
        i0.ɵɵtemplate(2, RoundMessageComponent_path_view_final_round_2_Template, 3, 4, "path-view-final-round", 2);
        i0.ɵɵtemplate(3, RoundMessageComponent_path_view_dropped_down_3_Template, 3, 9, "path-view-dropped-down", 3);
        i0.ɵɵtemplate(4, RoundMessageComponent_path_view_dropped_down_4_Template, 3, 9, "path-view-dropped-down", 3);
        i0.ɵɵtemplate(5, RoundMessageComponent_path_view_dropped_out_5_Template, 3, 9, "path-view-dropped-out", 3);
        i0.ɵɵtemplate(6, RoundMessageComponent_path_view_dropped_out_6_Template, 3, 3, "path-view-dropped-out", 4);
        i0.ɵɵtemplate(7, RoundMessageComponent_path_view_future_matches_cancelled_7_Template, 1, 0, "path-view-future-matches-cancelled", 3);
        i0.ɵɵtemplate(8, RoundMessageComponent_path_view_next_round_8_Template, 1, 3, "path-view-next-round", 5);
        i0.ɵɵtemplate(9, RoundMessageComponent_path_view_next_round_on_hold_9_Template, 1, 0, "path-view-next-round-on-hold", 3);
        i0.ɵɵelementEnd();
    } if (rf & 2) {
        i0.ɵɵproperty("ngClass", i0.ɵɵpureFunction2(10, _c6, !ctx.following && ctx.isSelf && (ctx.isPastFinal || ctx.isEliminated || ctx.isKnockedDown), ctx.following));
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngIf", !ctx.state.isEliminated() && ctx.nextRound);
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngIf", ctx.isPastFinal);
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngIf", ctx.isKnockedDown);
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngIf", ctx.isKnockedDownToThird);
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngIf", ctx.isPast && ctx.isThirdPlaceRound);
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngIf", ctx.isEliminated);
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngIf", ctx.allFutureMatchesCanceled);
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngIf", ctx.following);
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngIf", ctx.isFollowingOnHold);
    } }, directives: [i1$1.NgClass, i1$1.NgIf, FinalRoundComponent, i1$1.NgSwitch, i1$1.NgSwitchCase, DroppedDownComponent, DroppedOutComponent, FutureMatchesCancelledComponent, NextRoundComponent, NextRoundOnHoldComponent], pipes: [i3.TranslatePipe], encapsulation: 2, changeDetection: 0 });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(RoundMessageComponent, [{
        type: Component,
        args: [{
                selector: "path-view-round-message",
                templateUrl: "./round-message.component.html",
                styles: [],
                encapsulation: ViewEncapsulation.Emulated,
                changeDetection: ChangeDetectionStrategy.OnPush,
            }]
    }], function () { return [{ type: RoundService }, { type: DoubleEliminationService }]; }, { tournament: [{
            type: Input
        }], ownContestant: [{
            type: Input
        }], round: [{
            type: Input
        }], nextRound: [{
            type: Input
        }], following: [{
            type: Input
        }], participants: [{
            type: Input
        }], match: [{
            type: Input
        }], matches: [{
            type: Input
        }], state: [{
            type: Input
        }], isSelf: [{
            type: Input
        }], league: [{
            type: Input
        }] }); })();

function MatchStatusComponent_svg_icon_2_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelement(0, "svg-icon", 3);
} if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵproperty("icon", ctx_r0.indicatorIcon);
} }
class MatchStatusComponent {
    ngOnChanges(changes) {
        this.indicatorClassList = this.getIndicatorClassList();
        this.indicatorIcon = this.getIndicatorIcon();
    }
    getIndicatorIcon() {
        if (this.state.isFinal()) {
            return "crown";
        }
        if (this.state.isCurrent()) {
            return "dot";
        }
        if (this.state.isWin()) {
            return "checkmark";
        }
        if (this.state.isLoss() || this.state.isCancelled()) {
            return "skull";
        }
        return null;
    }
    getIndicatorClassList() {
        return {
            "is-win": this.state.isWin(),
            "is-loss": this.state.isLoss() || this.state.isCancelled(),
            "is-current": this.state.isCurrent(),
            "is-future": this.state.isFuture(),
            "is-following": this.state.isFollowing(),
            "is-final": this.state.isFinal(),
            "is-final--current": this.state.isFinal() && this.state.isCurrent(),
            "is-first-loss": !this.state.isFinal() && this.state.isLoss() && !this.state.isEliminated() && this.roundContestantId === this.contestantId,
        };
    }
}
MatchStatusComponent.ɵfac = function MatchStatusComponent_Factory(t) { return new (t || MatchStatusComponent)(); };
MatchStatusComponent.ɵcmp = i0.ɵɵdefineComponent({ type: MatchStatusComponent, selectors: [["path-view-match-status"]], inputs: { contestantId: "contestantId", roundContestantId: "roundContestantId", state: "state" }, features: [i0.ɵɵNgOnChangesFeature], decls: 3, vars: 2, consts: [[1, "match-status"], [1, "match-status__indicator", 3, "ngClass"], ["class", "match-status__indicator__icon", 3, "icon", 4, "ngIf"], [1, "match-status__indicator__icon", 3, "icon"]], template: function MatchStatusComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵelementStart(0, "div", 0);
        i0.ɵɵelementStart(1, "div", 1);
        i0.ɵɵtemplate(2, MatchStatusComponent_svg_icon_2_Template, 1, 1, "svg-icon", 2);
        i0.ɵɵelementEnd();
        i0.ɵɵelementEnd();
    } if (rf & 2) {
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngClass", ctx.indicatorClassList);
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngIf", ctx.indicatorIcon);
    } }, directives: [i1$1.NgClass, i1$1.NgIf, i1.SvgIconComponent], encapsulation: 2, changeDetection: 0 });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(MatchStatusComponent, [{
        type: Component,
        args: [{
                selector: "path-view-match-status",
                templateUrl: "./match-status.component.html",
                styles: [],
                encapsulation: ViewEncapsulation.Emulated,
                changeDetection: ChangeDetectionStrategy.OnPush,
            }]
    }], null, { contestantId: [{
            type: Input
        }], roundContestantId: [{
            type: Input
        }], state: [{
            type: Input
        }] }); })();

function MatchContestantsComponent_path_view_dropped_out_contestants_0_Template(rf, ctx) { if (rf & 1) {
    const _r4 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "path-view-dropped-out-contestants", 3);
    i0.ɵɵlistener("selected", function MatchContestantsComponent_path_view_dropped_out_contestants_0_Template_path_view_dropped_out_contestants_selected_0_listener($event) { i0.ɵɵrestoreView(_r4); const ctx_r3 = i0.ɵɵnextContext(); return ctx_r3.selectParticipant($event); });
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵproperty("round", ctx_r0.round)("participants", ctx_r0.participants)("tournament", ctx_r0.tournament)("contestantId", ctx_r0.selectedParticipant)("league", ctx_r0.league)("participantsDetails", ctx_r0.participantsDetails);
} }
function MatchContestantsComponent_path_view_possible_opponents_1_Template(rf, ctx) { if (rf & 1) {
    const _r6 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "path-view-possible-opponents", 4);
    i0.ɵɵlistener("selected", function MatchContestantsComponent_path_view_possible_opponents_1_Template_path_view_possible_opponents_selected_0_listener($event) { i0.ɵɵrestoreView(_r6); const ctx_r5 = i0.ɵɵnextContext(); return ctx_r5.selectParticipant($event); });
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵproperty("round", ctx_r1.round)("participants", ctx_r1.participants)("tournament", ctx_r1.tournament)("contestantId", ctx_r1.selectedParticipant)("league", ctx_r1.league);
} }
function MatchContestantsComponent_path_view_match_opponent_2_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelement(0, "path-view-match-opponent", 5);
} if (rf & 2) {
    const ctx_r2 = i0.ɵɵnextContext();
    i0.ɵɵproperty("opponentId", ctx_r2.round.opponent.id)("opponentMatches", ctx_r2.round.opponent.lastMatches)("league", ctx_r2.league)("participants", ctx_r2.participants);
} }
const MATCH_CONTESTANTS_MAX_DISPLAY = new InjectionToken("path-view.match-contestants.max-display");
class MatchContestantsComponent {
    constructor(matchService, store) {
        this.matchService = matchService;
        this.store = store;
    }
    ngOnChanges(changes) {
        if (this.round && this.participants) {
            if (this.round.droppedOutContestants) {
                this.round.droppedOutContestants = sortBy(this.round.droppedOutContestants, (contestantId) => {
                    if (this.participants[contestantId]) {
                        return this.participants[contestantId].seed;
                    }
                });
            }
            if (this.round.possibleOpponents) {
                this.round.possibleOpponents = sortBy(this.round.possibleOpponents, (contestantId) => {
                    if (this.participants[contestantId]) {
                        return this.participants[contestantId].seed;
                    }
                });
            }
        }
    }
    /**
     * Get the index of the current contestant in the match
     */
    getContestantIndex(id, match) {
        return this.matchService.getContestantIndexOfMatch(match, id);
    }
    /**
     * Get the index of the other contestant in the match
     */
    getOtherContestantIndex(id, match) {
        const contestantIndex = this.getContestantIndex(id, match);
        return contestantIndex ? 0 : 1;
    }
    selectParticipant(id) {
        this.store.dispatch(new SelectParticipantAction(this.league.id, id));
    }
}
MatchContestantsComponent.ɵfac = function MatchContestantsComponent_Factory(t) { return new (t || MatchContestantsComponent)(i0.ɵɵdirectiveInject(MatchService), i0.ɵɵdirectiveInject(i1$2.Store)); };
MatchContestantsComponent.ɵcmp = i0.ɵɵdefineComponent({ type: MatchContestantsComponent, selectors: [["path-view-match-contestants"]], inputs: { round: "round", participants: "participants", participantsDetails: "participantsDetails", tournament: "tournament", selectedParticipant: "selectedParticipant", league: "league" }, features: [i0.ɵɵNgOnChangesFeature], decls: 3, vars: 3, consts: [[3, "round", "participants", "tournament", "contestantId", "league", "participantsDetails", "selected", 4, "ngIf"], [3, "round", "participants", "tournament", "contestantId", "league", "selected", 4, "ngIf"], [3, "opponentId", "opponentMatches", "league", "participants", 4, "ngIf"], [3, "round", "participants", "tournament", "contestantId", "league", "participantsDetails", "selected"], [3, "round", "participants", "tournament", "contestantId", "league", "selected"], [3, "opponentId", "opponentMatches", "league", "participants"]], template: function MatchContestantsComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵtemplate(0, MatchContestantsComponent_path_view_dropped_out_contestants_0_Template, 1, 6, "path-view-dropped-out-contestants", 0);
        i0.ɵɵtemplate(1, MatchContestantsComponent_path_view_possible_opponents_1_Template, 1, 5, "path-view-possible-opponents", 1);
        i0.ɵɵtemplate(2, MatchContestantsComponent_path_view_match_opponent_2_Template, 1, 4, "path-view-match-opponent", 2);
    } if (rf & 2) {
        i0.ɵɵproperty("ngIf", ctx.round.droppedOutContestants && ctx.round.droppedOutContestants.length);
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngIf", ctx.round.possibleOpponents && ctx.round.possibleOpponents.length);
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngIf", ctx.round.opponent);
    } }, encapsulation: 2, changeDetection: 0 });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(MatchContestantsComponent, [{
        type: Component,
        args: [{
                selector: "path-view-match-contestants",
                templateUrl: "./match-contestants.component.html",
                encapsulation: ViewEncapsulation.Emulated,
                changeDetection: ChangeDetectionStrategy.OnPush,
            }]
    }], function () { return [{ type: MatchService }, { type: i1$2.Store }]; }, { round: [{
            type: Input
        }], participants: [{
            type: Input
        }], participantsDetails: [{
            type: Input
        }], tournament: [{
            type: Input
        }], selectedParticipant: [{
            type: Input
        }], league: [{
            type: Input
        }] }); })();

function RoundBoxComponent_ng_container_0_div_8_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelement(0, "div", 10);
} }
function RoundBoxComponent_ng_container_0_div_9_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelement(0, "div", 11);
} }
const _c0$5 = function (a0, a1, a2, a3, a4) { return { "is-win": a0, "is-loss": a1, "is-current": a2, "is-future": a3, "is-following": a4 }; };
function RoundBoxComponent_ng_container_0_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵelementStart(1, "article", 1);
    i0.ɵɵelementStart(2, "section", 2);
    i0.ɵɵelement(3, "path-view-match-status", 3);
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(4, "section");
    i0.ɵɵelement(5, "path-view-match", 4);
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(6, "section", 5);
    i0.ɵɵelement(7, "path-view-match-contestants", 6);
    i0.ɵɵelementEnd();
    i0.ɵɵtemplate(8, RoundBoxComponent_ng_container_0_div_8_Template, 1, 0, "div", 7);
    i0.ɵɵtemplate(9, RoundBoxComponent_ng_container_0_div_9_Template, 1, 0, "div", 8);
    i0.ɵɵelementEnd();
    i0.ɵɵelement(10, "path-view-round-message", 9);
    i0.ɵɵelementContainerEnd();
} if (rf & 2) {
    const participantsDetails_r1 = ctx.ngIf;
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngClass", i0.ɵɵpureFunction5(32, _c0$5, ctx_r0.state.isWin(), ctx_r0.state.isLoss() || ctx_r0.state.isCancelled(), ctx_r0.state.isCurrent(), ctx_r0.state.isFuture(), ctx_r0.state.isFollowing()));
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("state", ctx_r0.state)("contestantId", ctx_r0.selectedParticipant)("roundContestantId", ctx_r0.round.contestantId);
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("tournament", ctx_r0.tournament)("league", ctx_r0.league)("participants", ctx_r0.participants)("participantsDetails", participantsDetails_r1)("state", ctx_r0.state)("match", ctx_r0.match)("selectedParticipant", ctx_r0.selectedParticipant)("round", ctx_r0.round)("nextRound", ctx_r0.allRounds[ctx_r0.roundIndex + 1]);
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("tournament", ctx_r0.tournament)("round", ctx_r0.round)("participants", ctx_r0.participants)("participantsDetails", participantsDetails_r1)("selectedParticipant", ctx_r0.selectedParticipant)("league", ctx_r0.league);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", ctx_r0.allRounds[0] !== ctx_r0.round && ctx_r0.allRounds[ctx_r0.roundIndex - 1].contestantId === ctx_r0.round.contestantId);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", ctx_r0.allRounds[ctx_r0.allRounds.length - 1] !== ctx_r0.round && ctx_r0.allRounds[ctx_r0.roundIndex + 1].contestantId === ctx_r0.round.contestantId);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("tournament", ctx_r0.tournament)("ownContestant", ctx_r0.ownContestant)("match", ctx_r0.match)("matches", ctx_r0.matches)("round", ctx_r0.round)("nextRound", ctx_r0.allRounds[ctx_r0.roundIndex + 1])("state", ctx_r0.state)("participants", ctx_r0.participants)("league", ctx_r0.league)("isSelf", ctx_r0.round.contestantId == ctx_r0.selectedParticipant)("following", ctx_r0.round.contestantId != (ctx_r0.allRounds[ctx_r0.roundIndex + 1] == null ? null : ctx_r0.allRounds[ctx_r0.roundIndex + 1].contestantId) ? ctx_r0.participants[ctx_r0.allRounds[ctx_r0.roundIndex + 1] == null ? null : ctx_r0.allRounds[ctx_r0.roundIndex + 1].contestantId] : null);
} }
class RoundBoxComponent {
    constructor(matchService, roundService, store) {
        this.matchService = matchService;
        this.roundService = roundService;
        this.store = store;
    }
    ngOnChanges() {
        this.match = this.matchService.getMatch(this.matches, this.round.bracket, this.round.round, this.round.contestantId);
        this.state = this.roundService.getRoundState(this.selectedParticipant, this.match, this.round, this.allRounds[this.roundIndex + 1]);
        const playerIds = this.match.participants.map(({ id }) => id).filter(id => id > 0);
        if (playerIds.length) {
            if (isTeam(this.league)) {
                this.store.dispatch(new LoadTeamsOnceAction(playerIds));
                this.participantsDetails$ = this.store.select(getTeams(playerIds)).pipe(teamsToDictionary());
            }
            else {
                this.store.dispatch(new LoadPlayersOnceAction(playerIds));
                this.participantsDetails$ = this.store.select(getPlayers(playerIds)).pipe(playersToDictionary());
            }
        }
        else {
            this.participantsDetails$ = of({});
        }
    }
}
RoundBoxComponent.ɵfac = function RoundBoxComponent_Factory(t) { return new (t || RoundBoxComponent)(i0.ɵɵdirectiveInject(MatchService), i0.ɵɵdirectiveInject(RoundService), i0.ɵɵdirectiveInject(i1$2.Store)); };
RoundBoxComponent.ɵcmp = i0.ɵɵdefineComponent({ type: RoundBoxComponent, selectors: [["path-view-round-box"]], inputs: { tournament: "tournament", ownContestant: "ownContestant", league: "league", participants: "participants", allRounds: "allRounds", roundIndex: "roundIndex", round: "round", matches: "matches", selectedParticipant: "selectedParticipant" }, features: [i0.ɵɵNgOnChangesFeature], decls: 2, vars: 3, consts: [[4, "ngIf"], [1, "round-box", 3, "ngClass"], [1, "flex-stretch"], [1, "flex-center--vertical", 3, "state", "contestantId", "roundContestantId"], [3, "tournament", "league", "participants", "participantsDetails", "state", "match", "selectedParticipant", "round", "nextRound"], [1, "flex-auto"], [3, "tournament", "round", "participants", "participantsDetails", "selectedParticipant", "league"], ["class", "round-box__line round-box__line--top", 4, "ngIf"], ["class", "round-box__line round-box__line--bottom", 4, "ngIf"], [3, "tournament", "ownContestant", "match", "matches", "round", "nextRound", "state", "participants", "league", "isSelf", "following"], [1, "round-box__line", "round-box__line--top"], [1, "round-box__line", "round-box__line--bottom"]], template: function RoundBoxComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵtemplate(0, RoundBoxComponent_ng_container_0_Template, 11, 38, "ng-container", 0);
        i0.ɵɵpipe(1, "async");
    } if (rf & 2) {
        i0.ɵɵproperty("ngIf", i0.ɵɵpipeBind1(1, 1, ctx.participantsDetails$));
    } }, directives: [i1$1.NgIf, i1$1.NgClass, MatchStatusComponent, MatchComponent, MatchContestantsComponent, RoundMessageComponent], pipes: [i1$1.AsyncPipe], encapsulation: 2, changeDetection: 0 });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(RoundBoxComponent, [{
        type: Component,
        args: [{
                selector: "path-view-round-box",
                templateUrl: "./round-box.component.html",
                styles: [],
                encapsulation: ViewEncapsulation.Emulated,
                changeDetection: ChangeDetectionStrategy.OnPush,
            }]
    }], function () { return [{ type: MatchService }, { type: RoundService }, { type: i1$2.Store }]; }, { tournament: [{
            type: Input
        }], ownContestant: [{
            type: Input
        }], league: [{
            type: Input
        }], participants: [{
            type: Input
        }], allRounds: [{
            type: Input
        }], roundIndex: [{
            type: Input
        }], round: [{
            type: Input
        }], matches: [{
            type: Input
        }], selectedParticipant: [{
            type: Input
        }] }); })();

function ContestantListItemComponent_button_2_Template(rf, ctx) { if (rf & 1) {
    const _r3 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "button", 8);
    i0.ɵɵlistener("click", function ContestantListItemComponent_button_2_Template_button_click_0_listener() { i0.ɵɵrestoreView(_r3); const ctx_r2 = i0.ɵɵnextContext(); return ctx_r2.selected.emit(ctx_r2.id); });
    i0.ɵɵelement(1, "svg-icon", 9);
    i0.ɵɵelementEnd();
} }
function ContestantListItemComponent_premium_badge_8_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelement(0, "premium-badge");
} }
const _c0$4 = function (a0) { return { "is-eliminated": a0 }; };
class ContestantListItemComponent {
    constructor() {
        this.selected = new EventEmitter();
        this.showPoV = true;
    }
}
ContestantListItemComponent.ɵfac = function ContestantListItemComponent_Factory(t) { return new (t || ContestantListItemComponent)(); };
ContestantListItemComponent.ɵcmp = i0.ɵɵdefineComponent({ type: ContestantListItemComponent, selectors: [["path-view-contestant-list-item"]], inputs: { id: "id", name: "name", region: "region", isEliminated: "isEliminated", showPoV: "showPoV", participant: "participant", participantDetails: "participantDetails", league: "league" }, outputs: { selected: "selected" }, decls: 9, vars: 9, consts: [[1, "match-contestants__contestant-list__item", 3, "ngClass"], [1, "contestant__pov-switch-container"], ["class", "contestant__pov-switch", 3, "click", 4, "ngIf"], [3, "country"], [1, "match-contestants__contestant-list__contestant"], [1, "contestant-name"], [1, "contestant-name-inner", 3, "contestant", "league"], [4, "ngIf"], [1, "contestant__pov-switch", 3, "click"], ["icon", "eye", 1, "contestant__eye", "contestant__eye--small"]], template: function ContestantListItemComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵelementStart(0, "div", 0);
        i0.ɵɵelementStart(1, "span", 1);
        i0.ɵɵtemplate(2, ContestantListItemComponent_button_2_Template, 2, 0, "button", 2);
        i0.ɵɵelementEnd();
        i0.ɵɵelement(3, "country-flag", 3);
        i0.ɵɵelementStart(4, "span", 4);
        i0.ɵɵelementStart(5, "span", 5);
        i0.ɵɵelementStart(6, "path-view-contestant-link", 6);
        i0.ɵɵtext(7);
        i0.ɵɵelementEnd();
        i0.ɵɵtemplate(8, ContestantListItemComponent_premium_badge_8_Template, 1, 0, "premium-badge", 7);
        i0.ɵɵelementEnd();
        i0.ɵɵelementEnd();
        i0.ɵɵelementEnd();
    } if (rf & 2) {
        i0.ɵɵproperty("ngClass", i0.ɵɵpureFunction1(7, _c0$4, ctx.isEliminated));
        i0.ɵɵadvance(2);
        i0.ɵɵproperty("ngIf", ctx.showPoV);
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("country", ctx.region);
        i0.ɵɵadvance(3);
        i0.ɵɵproperty("contestant", ctx.participant)("league", ctx.league);
        i0.ɵɵadvance(1);
        i0.ɵɵtextInterpolate1(" ", ctx.name, " ");
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngIf", ctx.participantDetails == null ? null : ctx.participantDetails.premium);
    } }, directives: [i1$1.NgClass, i1$1.NgIf, i1.CountryFlagComponent, ContestantLinkComponent, i1.SvgIconComponent, i4.PremiumBadgeComponent], encapsulation: 2, changeDetection: 0 });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(ContestantListItemComponent, [{
        type: Component,
        args: [{
                selector: "path-view-contestant-list-item",
                templateUrl: "./contestant-list-item.component.html",
                encapsulation: ViewEncapsulation.Emulated,
                changeDetection: ChangeDetectionStrategy.OnPush,
            }]
    }], null, { selected: [{
            type: Output
        }], id: [{
            type: Input
        }], name: [{
            type: Input
        }], region: [{
            type: Input
        }], isEliminated: [{
            type: Input
        }], showPoV: [{
            type: Input
        }], participant: [{
            type: Input
        }], participantDetails: [{
            type: Input
        }], league: [{
            type: Input
        }] }); })();

function PossibleOpponentsComponent_path_view_contestant_list_item_5_Template(rf, ctx) { if (rf & 1) {
    const _r4 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "path-view-contestant-list-item", 5);
    i0.ɵɵlistener("selected", function PossibleOpponentsComponent_path_view_contestant_list_item_5_Template_path_view_contestant_list_item_selected_0_listener($event) { i0.ɵɵrestoreView(_r4); const ctx_r3 = i0.ɵɵnextContext(); return ctx_r3.selected.emit($event); });
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const id_r2 = ctx.$implicit;
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵproperty("id", id_r2)("name", ctx_r0.participants[id_r2] == null ? null : ctx_r0.participants[id_r2].name)("region", ctx_r0.participants[id_r2] == null ? null : ctx_r0.participants[id_r2].region)("showPoV", ctx_r0.contestantId !== id_r2)("isEliminated", false)("league", ctx_r0.league)("participant", ctx_r0.participants[id_r2]);
} }
const _c0$3 = function (a0, a1, a2, a3) { return { count: a0, player: a1, team: a2, type: a3 }; };
function PossibleOpponentsComponent_li_6_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "li", 6);
    i0.ɵɵtext(1);
    i0.ɵɵpipe(2, "translate");
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate1(" ", i0.ɵɵpipeBind2(2, 1, "path-view:other-contestants", i0.ɵɵpureFunction4(4, _c0$3, ctx_r1.round.possibleOpponents.length - (ctx_r1.configMaxDisplay - 1), !ctx_r1.tournament.isTeam, ctx_r1.tournament.isTeam, ctx_r1.tournament.isTeam ? "team" : "player")), " ");
} }
class PossibleOpponentsComponent {
    constructor(configMaxDisplay) {
        this.configMaxDisplay = configMaxDisplay;
        this.selected = new EventEmitter();
    }
    ngOnChanges(changes) {
        const displayAmount = this.configMaxDisplay < this.round.possibleOpponents.length ? this.configMaxDisplay - 1 : this.configMaxDisplay;
        this.displayContestants = this.round.possibleOpponents.slice(0, displayAmount);
    }
}
PossibleOpponentsComponent.ɵfac = function PossibleOpponentsComponent_Factory(t) { return new (t || PossibleOpponentsComponent)(i0.ɵɵdirectiveInject(MATCH_CONTESTANTS_MAX_DISPLAY)); };
PossibleOpponentsComponent.ɵcmp = i0.ɵɵdefineComponent({ type: PossibleOpponentsComponent, selectors: [["path-view-possible-opponents"]], inputs: { round: "round", participants: "participants", tournament: "tournament", contestantId: "contestantId", league: "league" }, outputs: { selected: "selected" }, features: [i0.ɵɵNgOnChangesFeature], decls: 7, vars: 5, consts: [[1, "match-contestants", "match-contestants--possible-opponents"], [1, "match-contestants__heading"], [1, "match-contestants__contestant-list"], [3, "id", "name", "region", "showPoV", "isEliminated", "league", "participant", "selected", 4, "ngFor", "ngForOf"], ["class", "match-contestants__contestant-list__item match-contestants__contestant-list__item--others", 4, "ngIf"], [3, "id", "name", "region", "showPoV", "isEliminated", "league", "participant", "selected"], [1, "match-contestants__contestant-list__item", "match-contestants__contestant-list__item--others"]], template: function PossibleOpponentsComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵelementStart(0, "div", 0);
        i0.ɵɵelementStart(1, "div", 1);
        i0.ɵɵtext(2);
        i0.ɵɵpipe(3, "translate");
        i0.ɵɵelementEnd();
        i0.ɵɵelementStart(4, "ul", 2);
        i0.ɵɵtemplate(5, PossibleOpponentsComponent_path_view_contestant_list_item_5_Template, 1, 7, "path-view-contestant-list-item", 3);
        i0.ɵɵtemplate(6, PossibleOpponentsComponent_li_6_Template, 3, 9, "li", 4);
        i0.ɵɵelementEnd();
        i0.ɵɵelementEnd();
    } if (rf & 2) {
        i0.ɵɵadvance(2);
        i0.ɵɵtextInterpolate1(" ", i0.ɵɵpipeBind1(3, 3, "path-view:possible-opponents"), " ");
        i0.ɵɵadvance(3);
        i0.ɵɵproperty("ngForOf", ctx.displayContestants);
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngIf", ctx.round.possibleOpponents.length > ctx.configMaxDisplay);
    } }, directives: [i1$1.NgForOf, i1$1.NgIf, ContestantListItemComponent], pipes: [i3.TranslatePipe], encapsulation: 2, changeDetection: 0 });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(PossibleOpponentsComponent, [{
        type: Component,
        args: [{
                selector: "path-view-possible-opponents",
                templateUrl: "./possible-opponents.component.html",
                encapsulation: ViewEncapsulation.Emulated,
                changeDetection: ChangeDetectionStrategy.OnPush,
            }]
    }], function () { return [{ type: undefined, decorators: [{
                type: Inject,
                args: [MATCH_CONTESTANTS_MAX_DISPLAY]
            }] }]; }, { selected: [{
            type: Output
        }], round: [{
            type: Input
        }], participants: [{
            type: Input
        }], tournament: [{
            type: Input
        }], contestantId: [{
            type: Input
        }], league: [{
            type: Input
        }] }); })();

function DroppedOutContestantsComponent_path_view_contestant_list_item_5_Template(rf, ctx) { if (rf & 1) {
    const _r4 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "path-view-contestant-list-item", 5);
    i0.ɵɵlistener("selected", function DroppedOutContestantsComponent_path_view_contestant_list_item_5_Template_path_view_contestant_list_item_selected_0_listener($event) { i0.ɵɵrestoreView(_r4); const ctx_r3 = i0.ɵɵnextContext(); return ctx_r3.selected.emit($event); });
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const id_r2 = ctx.$implicit;
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵproperty("id", id_r2)("name", ctx_r0.participants[id_r2] == null ? null : ctx_r0.participants[id_r2].name)("region", ctx_r0.participants[id_r2] == null ? null : ctx_r0.participants[id_r2].region)("showPoV", ctx_r0.contestantId !== id_r2)("isEliminated", true)("league", ctx_r0.league)("participant", ctx_r0.participants[id_r2])("participantDetails", ctx_r0.participantsDetails[id_r2]);
} }
const _c0$2 = function (a0, a1, a2, a3) { return { count: a0, player: a1, team: a2, type: a3 }; };
function DroppedOutContestantsComponent_li_6_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "li", 6);
    i0.ɵɵtext(1);
    i0.ɵɵpipe(2, "translate");
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate1(" ", i0.ɵɵpipeBind2(2, 1, "path-view:other-contestants", i0.ɵɵpureFunction4(4, _c0$2, ctx_r1.round.droppedOutContestants.length - (ctx_r1.configMaxDisplay - 1), !ctx_r1.tournament.isTeam, ctx_r1.tournament.isTeam, ctx_r1.tournament.isTeam ? "team" : "player")), " ");
} }
const _c1$1 = function (a0, a1, a2) { return { player: a0, team: a1, type: a2 }; };
class DroppedOutContestantsComponent {
    constructor(configMaxDisplay) {
        this.configMaxDisplay = configMaxDisplay;
        this.selected = new EventEmitter();
    }
    ngOnChanges(changes) {
        const displayAmount = this.configMaxDisplay < this.round.droppedOutContestants.length ? this.configMaxDisplay - 1 : this.configMaxDisplay;
        this.displayContestants = this.round.droppedOutContestants.slice(0, displayAmount);
    }
}
DroppedOutContestantsComponent.ɵfac = function DroppedOutContestantsComponent_Factory(t) { return new (t || DroppedOutContestantsComponent)(i0.ɵɵdirectiveInject(MATCH_CONTESTANTS_MAX_DISPLAY)); };
DroppedOutContestantsComponent.ɵcmp = i0.ɵɵdefineComponent({ type: DroppedOutContestantsComponent, selectors: [["path-view-dropped-out-contestants"]], inputs: { round: "round", participants: "participants", participantsDetails: "participantsDetails", tournament: "tournament", contestantId: "contestantId", league: "league" }, outputs: { selected: "selected" }, features: [i0.ɵɵNgOnChangesFeature], decls: 7, vars: 10, consts: [[1, "match-contestants", "match-contestants--droped-out"], [1, "match-contestants__heading"], [1, "match-contestants__contestant-list"], [3, "id", "name", "region", "showPoV", "isEliminated", "league", "participant", "participantDetails", "selected", 4, "ngFor", "ngForOf"], ["class", "match-contestants__contestant-list__item match-contestants__contestant-list__item--others", 4, "ngIf"], [3, "id", "name", "region", "showPoV", "isEliminated", "league", "participant", "participantDetails", "selected"], [1, "match-contestants__contestant-list__item", "match-contestants__contestant-list__item--others"]], template: function DroppedOutContestantsComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵelementStart(0, "div", 0);
        i0.ɵɵelementStart(1, "div", 1);
        i0.ɵɵtext(2);
        i0.ɵɵpipe(3, "translate");
        i0.ɵɵelementEnd();
        i0.ɵɵelementStart(4, "ul", 2);
        i0.ɵɵtemplate(5, DroppedOutContestantsComponent_path_view_contestant_list_item_5_Template, 1, 8, "path-view-contestant-list-item", 3);
        i0.ɵɵtemplate(6, DroppedOutContestantsComponent_li_6_Template, 3, 9, "li", 4);
        i0.ɵɵelementEnd();
        i0.ɵɵelementEnd();
    } if (rf & 2) {
        i0.ɵɵadvance(2);
        i0.ɵɵtextInterpolate1(" ", i0.ɵɵpipeBind2(3, 3, "path-view:top-contestants-eliminated", i0.ɵɵpureFunction3(6, _c1$1, !ctx.tournament.isTeam, ctx.tournament.isTeam, ctx.tournament.isTeam ? "team" : "player")), " ");
        i0.ɵɵadvance(3);
        i0.ɵɵproperty("ngForOf", ctx.displayContestants);
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngIf", ctx.round.droppedOutContestants.length > ctx.configMaxDisplay);
    } }, directives: [i1$1.NgForOf, i1$1.NgIf, ContestantListItemComponent], pipes: [i3.TranslatePipe], encapsulation: 2, changeDetection: 0 });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DroppedOutContestantsComponent, [{
        type: Component,
        args: [{
                selector: "path-view-dropped-out-contestants",
                templateUrl: "./dropped-out-contestants.component.html",
                encapsulation: ViewEncapsulation.Emulated,
                changeDetection: ChangeDetectionStrategy.OnPush,
            }]
    }], function () { return [{ type: undefined, decorators: [{
                type: Inject,
                args: [MATCH_CONTESTANTS_MAX_DISPLAY]
            }] }]; }, { selected: [{
            type: Output
        }], round: [{
            type: Input
        }], participants: [{
            type: Input
        }], participantsDetails: [{
            type: Input
        }], tournament: [{
            type: Input
        }], contestantId: [{
            type: Input
        }], league: [{
            type: Input
        }] }); })();

class RoundBuilderService {
    constructor(matchService, doubleElmination, roundService) {
        this.matchService = matchService;
        this.doubleElmination = doubleElmination;
        this.roundService = roundService;
    }
    /**
     * Build the rounds of a given contestants tournament path.
     * This includes past and future rounds.
     * @params matches                  All matches in traversable order.
     * @params contestantId             Id of contestant whose path to follow.
     * @returns The rounds of the given contestant.
     */
    buildRounds(matches, contestantId) {
        if (!matches || !matches.length) {
            return [];
        }
        const isDoubleElimination = this.doubleElmination.isDoubleEliminationCupMode(matches);
        const hasThirdPlaceMatch = this.matchService.hasThirdPlaceMatch(matches);
        // Result.
        let rounds = [];
        const roundMatches = [];
        // The contestant we follow at given round.
        let currentContestant = contestantId;
        // 1. Find first match.
        const firstMatch = this.findFirstMatch(matches, currentContestant);
        rounds.push(this.buildRoundSkeleton(matches, firstMatch, currentContestant));
        roundMatches.push(firstMatch);
        // 2. Find following matches
        let nextMatch = firstMatch;
        let oldContestant;
        while (nextMatch) {
            oldContestant = currentContestant;
            currentContestant = this.getNewContestantId(matches, nextMatch, currentContestant, isDoubleElimination, hasThirdPlaceMatch);
            nextMatch = this.findNextMatchInPath(matches, nextMatch, currentContestant, hasThirdPlaceMatch);
            if (nextMatch) {
                rounds.push(this.buildRoundSkeleton(matches, nextMatch, currentContestant));
                roundMatches.push(nextMatch);
            }
        }
        rounds = this.filterCancelledMatchesWithoutSuccessor(rounds, roundMatches);
        rounds = this.mapLosersToRounds(matches, rounds);
        rounds = this.mapOpponentsToRounds(matches, rounds);
        rounds = this.mapPossibleOpponentsToRounds(matches, rounds);
        rounds = this.filterRoundsForOptimalPath(matches, rounds);
        return rounds;
    }
    filterRoundsForOptimalPath(matches, rounds) {
        if (!this.doubleElmination.isDoubleDoubleFinalCupMode(matches)) {
            return rounds;
        }
        const hasLostAMatch = filter(rounds, (round) => {
            return round.droppedOutContestants && round.droppedOutContestants.indexOf(first(rounds).contestantId) !== -1;
        }).length > 0;
        /**
         * If contestant hasn't lost a match and last round is still present, filter it out.
         */
        if (!hasLostAMatch && last(rounds).round === last(matches).round) {
            return slice(rounds, 0, rounds.length - 1);
        }
        return rounds;
    }
    /**
     * Find next match of contestant in path.
     * @param matches         All matches in traversable order.
     * @param currentMatch    The match to get the next match for.
     * @param contestantId    ContestantId we follow at current match.
     * @param hasThirdPlaceMatch
     * @return The next match in path.
     */
    findNextMatchInPath(matches, currentMatch, contestantId, hasThirdPlaceMatch) {
        // Third place match to finals.
        if (hasThirdPlaceMatch && last(matches) === currentMatch) {
            return findLast(matches, (match) => {
                return match.bracket === 0 && match.round === currentMatch.round;
            });
        }
        if (currentMatch.state === "closed") {
            const currentContestant = find(currentMatch.participants, (participant) => {
                return participant.id === contestantId;
            });
            if (!currentContestant) {
                return null;
            }
            if (currentContestant.place > 1) {
                // @TODO find contestant even if he's not pushed in next round yet.
                return find(matches, (match) => {
                    return (match.round > currentMatch.round &&
                        find(match.participants, (participant) => {
                            return participant.id === contestantId;
                        }));
                });
            }
        }
        return this.matchService.getNextMatch(matches, currentMatch.id);
    }
    /**
     * Build the skeleton of a round.
     * PossibleOpponents, droppedOutContestants and opponent will be set null
     * and must be processed later on.
     * @param matches       All matches in traversable order.
     * @param matchInRound  Match of contestant in round.
     * @param contestantId  Id of contestant we follow in given round.
     */
    buildRoundSkeleton(matches, matchInRound, contestantId) {
        return {
            bracket: matchInRound.bracket,
            round: matchInRound.round,
            of: this.roundService.getAmountOfContestants(matches, matchInRound.bracket, matchInRound.round),
            isFinal: this.isFinalMatch(matches, matchInRound),
            contestantId: contestantId,
            possibleOpponents: null,
            droppedOutContestants: null,
            opponent: null,
        };
    }
    /**
     * Find first match for contestant.
     * @param matches       All matches in traversable order.
     * @param contestantId  Id of contestant to find match for.
     * @return First match of contestant or undefined.
     */
    findFirstMatch(matches, contestantId) {
        return find(matches, (match) => {
            return find(match.participants, (participant) => {
                return participant.id === contestantId;
            });
        });
    }
    /**
     * Return true if match is final match.
     * @param matches   All matches in traversable order.
     * @param match     Match to check.
     */
    isFinalMatch(matches, match) {
        if (this.doubleElmination.isDoubleEliminationCupMode(matches)) {
            return this.doubleElmination.isGrandFinal(match, matches);
        }
        const finalMatchIndex = matches.length - (this.matchService.hasThirdPlaceMatch(matches) ? 2 : 1);
        return matches[finalMatchIndex] === match;
    }
    /**
     * Gets new contestantId depending on outcome of match.
     * @param matches       All matches in traversable order.
     * @param match         The current match.
     * @param contestantId  Current contestant.
     * @param isDoubleElimination
     * @param hasThirdPlaceMatch
     * @returns The new contestant id.
     */
    getNewContestantId(matches, match, contestantId, isDoubleElimination, hasThirdPlaceMatch) {
        const contestantInMatch = find(match.participants, (participant) => {
            return participant.id === contestantId;
        });
        if (match.state === "cancelled") {
            // Get next match. If contestant is already in there, follow him. Else follow nobody (yet).
            const nextMatch = this.matchService.getNextMatch(matches, match.id);
            if (nextMatch) {
                const contestant = find(nextMatch.participants, (participant) => participant.id);
                return contestant ? contestant.id : 0;
            }
            return 0;
        }
        // Current match is third place match.
        if (hasThirdPlaceMatch && match.round === last(matches).round && match.bracket === 1) {
            // Get semi final winner.
            return this.getSemiFinalWinner(matches, contestantId);
        }
        // Lost in third place match semi finals.
        if (hasThirdPlaceMatch && match.round === last(matches).round - 1) {
            // Get semi final winner.
            return contestantId;
        }
        // Lost but not eliminated.
        if (contestantInMatch && contestantInMatch.place === 2 && (!isDoubleElimination || match.bracket === 1)) {
            return find(match.participants, (participant) => {
                return participant.id !== contestantId;
            }).id;
        }
        return contestantId;
    }
    /**
     * Map dropped out contestants to rounds.
     * @param matches       All matches in traversable order.
     * @param rounds        All rounds.
     * @returns The rounds with dropped out contestants mapped to them.
     */
    mapLosersToRounds(matches, rounds) {
        forEach(rounds, (round) => {
            // 1. Check if round is past.
            const droppedOutContestants = [];
            const roundMatches = filter(matches, (match) => {
                return match.round === round.round && match.bracket === round.bracket;
            });
            const lastRoundWithContestant = findLast(rounds.slice(0, rounds.indexOf(round) + 1), currentRound => currentRound.contestantId);
            const matchInRound = lastRoundWithContestant &&
                this.matchService.getMatch(matches, round.bracket, round.round, lastRoundWithContestant.contestantId);
            // If state in round is closed, that round is in the past.
            if (matchInRound && this.matchService.isClosedLike(matchInRound.state)) {
                forEach(roundMatches, (match) => {
                    // Contestants of cancelled matches drop out of the tournament.
                    if (match.state === "cancelled") {
                        forEach(match.participants, (participant) => {
                            if (participant.id !== 0) {
                                droppedOutContestants.push(participant.id);
                            }
                        });
                        return;
                    }
                    forEach(match.participants, (participant) => {
                        if (participant.place === 2 && participant.id !== 0) {
                            droppedOutContestants.push(participant.id);
                        }
                    });
                });
                round.droppedOutContestants = droppedOutContestants;
            }
        });
        return rounds;
    }
    /**
     * Map opponents and their last matches to rounds.
     * @param matches       All matches in traversable order.
     * @param rounds        All rounds.
     * @returns The rounds with opponents and their last matches mapped to them.
     */
    mapOpponentsToRounds(matches, rounds) {
        forEach(rounds, (round) => {
            // 1. Check if round is present.
            let opponent = null;
            const roundMatch = find(matches, (match) => {
                return (match.bracket === round.bracket &&
                    match.round === round.round &&
                    find(match.participants, (participant) => {
                        return participant.id !== 0 && participant.id === round.contestantId;
                    }));
            });
            // Match must not be closed or cancelled.
            if (!roundMatch || this.matchService.isClosedLike(roundMatch.state)) {
                return;
            }
            const matchOpponent = find(roundMatch.participants, (participant) => {
                return participant.id !== round.contestantId;
            });
            // Opponent must be a player.
            if (!matchOpponent || matchOpponent.id === 0) {
                return;
            }
            opponent = {
                id: matchOpponent.id,
                lastMatches: null,
            };
            // Get opponents last matches. Sort by latest match first.
            opponent.lastMatches = sortBy(filter(matches, (match) => {
                return (match.round < round.round &&
                    !!find(match.participants, (participant) => {
                        return participant.id === matchOpponent.id;
                    }));
            }), ["round"]).reverse();
            round.opponent = opponent;
        });
        return rounds;
    }
    /**
     * Map opponents the current contestant in round
     * may meet in that given round to the round.
     * @param matches   All matches in traversable order.
     * @param rounds    All round skeletons.
     * @returns The round skeletons with possible opponents set.
     */
    mapPossibleOpponentsToRounds(matches, rounds) {
        return rounds.map((round, index) => {
            // Map possible opponents if round is future!
            if (this.roundIsFuture(matches, rounds, round)) {
                return assign({}, round, {
                    possibleOpponents: this.getPossibleOpponents(matches, round.contestantId, round.round, this.findOpenMatch(matches, rounds, round)),
                });
            }
            return round;
        });
    }
    /**
     * Determine if round is future.
     * @param matches       All matches.
     * @param rounds        All rounds.
     * @param currentRound  The round to check.
     * @returns True if currentRound is future.
     */
    roundIsFuture(matches, rounds, currentRound) {
        // This means contestant is undetermined. We wait for new contestant. Must be future.
        if (currentRound.contestantId === 0) {
            return true;
        }
        const matchInRound = find(matches, (match) => match.bracket === currentRound.bracket &&
            match.round === currentRound.round &&
            find(match.participants, (participant) => participant.id === currentRound.contestantId));
        // If contestant isn't in round yet round must be future.
        if (!matchInRound) {
            return true;
        }
        // If contestant is in match but match is closed like it cannot be future.
        if (matchInRound && this.matchService.isClosedLike(matchInRound.state)) {
            return false;
        }
        // We found match because our contestant is in there already.
        if (matchInRound) {
            // Check if opponent is in match aswell. If not it's future.
            return !!find(matchInRound.participants, (participant) => {
                return participant.id === 0;
            });
        }
        return false;
    }
    /**
     * Find open match of contestant path within a given future round.
     * @param matches       All matches.
     * @param rounds        All rounds.
     * @param currentRound  The future round to find the match for.
     * @returns The open match.
     */
    findOpenMatch(matches, rounds, currentRound) {
        let res;
        forEachRight(slice(rounds, 0, rounds.indexOf(currentRound) + 1), (round) => {
            const matchInRound = find(matches, (match) => {
                return (match.round === round.round &&
                    find(match.participants, (participant) => participant.id !== 0 && participant.id === round.contestantId));
            });
            if (!matchInRound) {
                return;
            }
            if (matchInRound.round === currentRound.round) {
                res = matchInRound;
                return false;
            }
            // Found match but in previous round. Traverse back up until we find current round.
            let nextMatch = matchInRound;
            if (this.matchService.isClosedLike(nextMatch.state)) {
                while (nextMatch && !this.matchService.isClosedLike(nextMatch.state)) {
                    nextMatch = this.matchService.getNextMatch(matches, nextMatch.id);
                }
            }
            res = nextMatch;
            return false;
        });
        return res;
    }
    /**
     * Get possible opponents for given round & contestantId.
     * @param matches           All matches in traversable order.
     * @param contestantId      The id of the contestant to get possible opponents for.
     * @param round             The round to get possible opponents for.
     * @param openMatch
     * @returns An array of ids of possible opponents for the given contestant and round.
     */
    getPossibleOpponents(matches, contestantId, round, openMatch) {
        let possibleOpponents = [];
        if (!openMatch) {
            return [];
        }
        let blacklist = [openMatch.id];
        // 2. Traverse up to match in round we want to find possible opponents for.
        blacklist = this.populateBlacklist(matches, round, blacklist, openMatch);
        const mostRightMatchId = blacklist.pop();
        possibleOpponents = this.populatePossibleOpponents(matches, mostRightMatchId, blacklist, possibleOpponents);
        return filter(possibleOpponents, opponentId => opponentId !== contestantId);
    }
    /**
     * Traverse up from currentMatch to round and add all occuring matches to a blacklist.
     * @param matches           All matches in traversable order.
     * @param round             The round to traverse up to.
     * @param blacklist         The blacklist. Usually provided as an empty array to start off with.
     * @param currentMatch      The match to start traversing from.
     * @returns An array containing matchIds that we passed when traversing up.
     */
    populateBlacklist(matches, round, blacklist, currentMatch) {
        if (currentMatch.round < round) {
            const nextMatch = this.matchService.getNextMatch(matches, currentMatch.id);
            blacklist.push(nextMatch.id);
            return this.populateBlacklist(matches, round, blacklist, nextMatch);
        }
        return blacklist;
    }
    /**
     * Recursively traverse down from a given match (provided by matchId), going all
     * possible paths but the blacklisted one.
     * @param matches               All matches in traversable order.
     * @param matchId               Id of the match we start traversing down from.
     * @param blacklist             MatchIds to not traverse down to.
     * @param possibleOpponents    Possible opponents. Usually provided as an empty array to start off with.
     * @returns An array containing ContestantIds that are possible opponents for the given match.
     */
    populatePossibleOpponents(matches, matchId, blacklist, possibleOpponents) {
        // Set true when child is lower bracket while parent is upper bracket.
        let ubTransition = false;
        const parents = filter(this.matchService.getParents(matches, matchId), (parentMatch) => {
            return indexOf(blacklist, parentMatch && parentMatch.id) === -1;
        });
        const currentMatch = find(matches, (match) => match.id === matchId);
        if (parents.length === 0) {
            forEach(currentMatch.participants, (participant) => {
                if (participant.id !== 0 && !includes(possibleOpponents, participant.id)) {
                    possibleOpponents.push(participant.id);
                }
            });
        }
        forEach(parents, (parentMatch) => {
            if (!parentMatch) {
                return;
            }
            if (!this.matchService.isClosedLike(parentMatch.state)) {
                possibleOpponents = this.populatePossibleOpponents(matches, parentMatch.id, [], possibleOpponents);
            }
            else {
                ubTransition = false;
                if (currentMatch.bracket === 1 && parentMatch.bracket === 0) {
                    ubTransition = true;
                }
                // If match is cancelled, there is no winner.
                if (parentMatch.state === "cancelled") {
                    return;
                }
                const participant = find(parentMatch.participants, (p) => {
                    return p.place === (ubTransition ? 2 : 1);
                });
                if (participant && participant.id !== 0 && !includes(possibleOpponents, participant.id)) {
                    possibleOpponents.push(participant.id);
                }
            }
        });
        return possibleOpponents;
    }
    /**
     * Filter a sequence of cancelled matches that lead to no non-cancelled successor.
     * @param rounds        All rounds in same order as roundMatches.
     * @param roundMatches  The matches for rounds in same order as rounds.
     * @returns The filtered rounds.
     */
    filterCancelledMatchesWithoutSuccessor(rounds, roundMatches) {
        forEachRight(roundMatches, (match) => {
            if (match.state !== "cancelled") {
                return false;
            }
            rounds.pop();
        });
        return rounds;
    }
    /**
     * Get the id of the semi final winner of given contestant.
     * @param matches           All matches in traversable order.
     * @param contestantId      Id of contestant to get the semi final winner for.
     * @return Id of semi final winner.
     */
    getSemiFinalWinner(matches, contestantId) {
        const semiFinalRound = last(matches).round - 1;
        const semiFinalMatch = find(matches, (match) => {
            return (match.round === semiFinalRound &&
                find(match.participants, (participant) => {
                    return participant.id === contestantId;
                }));
        });
        const semiFinalWinner = find(semiFinalMatch.participants, (participant) => participant.place === 1);
        return semiFinalWinner ? semiFinalWinner.id : 0;
    }
}
RoundBuilderService.ɵfac = function RoundBuilderService_Factory(t) { return new (t || RoundBuilderService)(i0.ɵɵinject(MatchService), i0.ɵɵinject(DoubleEliminationService), i0.ɵɵinject(RoundService)); };
RoundBuilderService.ɵprov = i0.ɵɵdefineInjectable({ token: RoundBuilderService, factory: RoundBuilderService.ɵfac });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(RoundBuilderService, [{
        type: Injectable
    }], function () { return [{ type: MatchService }, { type: DoubleEliminationService }, { type: RoundService }]; }, null); })();

function PathViewComponent_ng_container_1_path_view_round_box_3_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelement(0, "path-view-round-box", 6);
} if (rf & 2) {
    const round_r3 = ctx.$implicit;
    const $index_r4 = ctx.index;
    const ctx_r2 = i0.ɵɵnextContext(2);
    i0.ɵɵproperty("tournament", ctx_r2.tournament)("ownContestant", ctx_r2.ownContestant)("league", ctx_r2.league)("participants", ctx_r2.participants)("allRounds", ctx_r2.rounds)("roundIndex", $index_r4)("round", round_r3)("matches", ctx_r2.matches)("selectedParticipant", ctx_r2.selectedParticipant);
} }
function PathViewComponent_ng_container_1_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵelementStart(1, "div", 3);
    i0.ɵɵelement(2, "cup-page-participant-switcher", 4);
    i0.ɵɵelementEnd();
    i0.ɵɵtemplate(3, PathViewComponent_ng_container_1_path_view_round_box_3_Template, 1, 9, "path-view-round-box", 5);
    i0.ɵɵelementContainerEnd();
} if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("leagueId", ctx_r0.league.id);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngForOf", ctx_r0.rounds)("ngForTrackBy", ctx_r0.trackByIndex);
} }
function PathViewComponent_div_2_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 7);
    i0.ɵɵtext(1);
    i0.ɵɵpipe(2, "translate");
    i0.ɵɵelementEnd();
} if (rf & 2) {
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate1(" ", i0.ɵɵpipeBind1(2, 1, "path-view:not-started-yet"), " ");
} }
class PathViewComponent {
    constructor(tournamentService, roundBuilder) {
        this.tournamentService = tournamentService;
        this.roundBuilder = roundBuilder;
    }
    ngOnChanges(changes) {
        this.areMatchesPopulated =
            filter(this.matches, match => {
                return match.participants && match.participants.find(participant => Boolean(participant.id));
            }).length > 0;
        if (this.selectedParticipant && this.matches && (changes.matches || changes.selectedParticipant)) {
            this.rounds = this.roundBuilder.buildRounds(this.matches, this.selectedParticipant);
        }
        if (!this.league || !this.matches || !this.participants) {
            return;
        }
        this.tournament = this.tournamentService.getTournamentInformation(this.matches, this.league);
    }
    trackByIndex(index, item) {
        return index;
    }
}
PathViewComponent.ɵfac = function PathViewComponent_Factory(t) { return new (t || PathViewComponent)(i0.ɵɵdirectiveInject(TournamentService), i0.ɵɵdirectiveInject(RoundBuilderService)); };
PathViewComponent.ɵcmp = i0.ɵɵdefineComponent({ type: PathViewComponent, selectors: [["path-view"]], inputs: { league: "league", matches: "matches", participants: "participants", selectedParticipant: "selectedParticipant", ownContestant: "ownContestant" }, features: [i0.ɵɵNgOnChangesFeature], decls: 3, vars: 2, consts: [[1, "c-path-view"], [4, "ngIf"], ["class", "round-box__no-bracket", 4, "ngIf"], [1, "cup-page__header"], ["pageType", "pathView", 3, "leagueId"], [3, "tournament", "ownContestant", "league", "participants", "allRounds", "roundIndex", "round", "matches", "selectedParticipant", 4, "ngFor", "ngForOf", "ngForTrackBy"], [3, "tournament", "ownContestant", "league", "participants", "allRounds", "roundIndex", "round", "matches", "selectedParticipant"], [1, "round-box__no-bracket"]], template: function PathViewComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵelementStart(0, "div", 0);
        i0.ɵɵtemplate(1, PathViewComponent_ng_container_1_Template, 4, 3, "ng-container", 1);
        i0.ɵɵtemplate(2, PathViewComponent_div_2_Template, 3, 3, "div", 2);
        i0.ɵɵelementEnd();
    } if (rf & 2) {
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngIf", ctx.areMatchesPopulated);
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngIf", !ctx.areMatchesPopulated);
    } }, directives: [i1$1.NgIf, i4$1.ParticipantSwitcherComponent, i1$1.NgForOf, RoundBoxComponent], pipes: [i3.TranslatePipe], encapsulation: 2, changeDetection: 0 });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(PathViewComponent, [{
        type: Component,
        args: [{
                selector: "path-view",
                templateUrl: "./path-view.component.html",
                styles: [],
                encapsulation: ViewEncapsulation.Emulated,
                changeDetection: ChangeDetectionStrategy.OnPush,
            }]
    }], function () { return [{ type: TournamentService }, { type: RoundBuilderService }]; }, { league: [{
            type: Input
        }], matches: [{
            type: Input
        }], participants: [{
            type: Input
        }], selectedParticipant: [{
            type: Input
        }], ownContestant: [{
            type: Input
        }] }); })();

function MatchOpponentComponent_div_0_ng_container_1_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementContainer(0);
} }
function MatchOpponentComponent_div_0_ng_container_2_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementContainer(0);
} }
function MatchOpponentComponent_div_0_ng_container_3_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementContainer(0);
} }
function MatchOpponentComponent_div_0_ng_template_4_img_5_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelement(0, "img", 18);
} if (rf & 2) {
    const opponent_r1 = i0.ɵɵnextContext(2).ngIf;
    i0.ɵɵproperty("src", opponent_r1.entry.photo, i0.ɵɵsanitizeUrl);
} }
function MatchOpponentComponent_div_0_ng_template_4_img_6_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelement(0, "img", 18);
} if (rf & 2) {
    const opponent_r1 = i0.ɵɵnextContext(2).ngIf;
    i0.ɵɵproperty("src", opponent_r1.entry.logo, i0.ɵɵsanitizeUrl);
} }
function MatchOpponentComponent_div_0_ng_template_4_li_18_ng_container_2_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵtext(1);
    i0.ɵɵpipe(2, "translate");
    i0.ɵɵelementContainerEnd();
} if (rf & 2) {
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate1(" ", i0.ɵɵpipeBind1(2, 1, "path-view:win"), " ");
} }
function MatchOpponentComponent_div_0_ng_template_4_li_18_ng_container_3_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵtext(1);
    i0.ɵɵpipe(2, "translate");
    i0.ɵɵelementContainerEnd();
} if (rf & 2) {
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate1(" ", i0.ɵɵpipeBind1(2, 1, "path-view:loss"), " ");
} }
function MatchOpponentComponent_div_0_ng_template_4_li_18_ng_container_6_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵelementStart(1, "path-view-contestant-link", 8);
    i0.ɵɵelementStart(2, "span", 22);
    i0.ɵɵtext(3);
    i0.ɵɵelementEnd();
    i0.ɵɵelementEnd();
    i0.ɵɵelementContainerEnd();
} if (rf & 2) {
    const match_r16 = i0.ɵɵnextContext().$implicit;
    const ctx_r19 = i0.ɵɵnextContext(3);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("contestant", ctx_r19.participants[match_r16.versusContestant])("league", ctx_r19.league);
    i0.ɵɵadvance(2);
    i0.ɵɵtextInterpolate1(" ", ctx_r19.participants[match_r16.versusContestant] == null ? null : ctx_r19.participants[match_r16.versusContestant].name, " ");
} }
function MatchOpponentComponent_div_0_ng_template_4_li_18_ng_container_7_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵtext(1);
    i0.ɵɵpipe(2, "translate");
    i0.ɵɵelementContainerEnd();
} if (rf & 2) {
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate1(" ", i0.ɵɵpipeBind1(2, 1, "path-view:defwin"), " ");
} }
const _c0$1 = function (a0, a1) { return { "is-win": a0, "is-loss": a1 }; };
function MatchOpponentComponent_div_0_ng_template_4_li_18_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "li", 19);
    i0.ɵɵelementStart(1, "span", 20);
    i0.ɵɵtemplate(2, MatchOpponentComponent_div_0_ng_template_4_li_18_ng_container_2_Template, 3, 3, "ng-container", 21);
    i0.ɵɵtemplate(3, MatchOpponentComponent_div_0_ng_template_4_li_18_ng_container_3_Template, 3, 3, "ng-container", 21);
    i0.ɵɵelementEnd();
    i0.ɵɵtext(4);
    i0.ɵɵpipe(5, "translate");
    i0.ɵɵtemplate(6, MatchOpponentComponent_div_0_ng_template_4_li_18_ng_container_6_Template, 4, 3, "ng-container", 21);
    i0.ɵɵtemplate(7, MatchOpponentComponent_div_0_ng_template_4_li_18_ng_container_7_Template, 3, 3, "ng-container", 21);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const match_r16 = ctx.$implicit;
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngClass", i0.ɵɵpureFunction2(8, _c0$1, match_r16.isWin, match_r16.isLoss));
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", match_r16.isWin);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", match_r16.isLoss);
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate1(" ", i0.ɵɵpipeBind1(5, 6, "path-view:vs"), " ");
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("ngIf", !match_r16.isDefaultWin);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", match_r16.isDefaultWin);
} }
function MatchOpponentComponent_div_0_ng_template_4_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "span", 6);
    i0.ɵɵtext(1);
    i0.ɵɵpipe(2, "translate");
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(3, "div", 7);
    i0.ɵɵelementStart(4, "path-view-contestant-link", 8);
    i0.ɵɵtemplate(5, MatchOpponentComponent_div_0_ng_template_4_img_5_Template, 1, 1, "img", 9);
    i0.ɵɵtemplate(6, MatchOpponentComponent_div_0_ng_template_4_img_6_Template, 1, 1, "img", 9);
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(7, "div", 10);
    i0.ɵɵelementStart(8, "div", 11);
    i0.ɵɵelement(9, "country-flag", 12);
    i0.ɵɵelementStart(10, "span", 13);
    i0.ɵɵelementStart(11, "path-view-contestant-link", 8);
    i0.ɵɵelementStart(12, "span", 14);
    i0.ɵɵtext(13);
    i0.ɵɵelementEnd();
    i0.ɵɵelementEnd();
    i0.ɵɵelementEnd();
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(14, "ul", 15);
    i0.ɵɵelementStart(15, "li", 16);
    i0.ɵɵtext(16);
    i0.ɵɵpipe(17, "translate");
    i0.ɵɵelementEnd();
    i0.ɵɵtemplate(18, MatchOpponentComponent_div_0_ng_template_4_li_18_Template, 8, 11, "li", 17);
    i0.ɵɵelementEnd();
    i0.ɵɵelementEnd();
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const opponent_r1 = i0.ɵɵnextContext().ngIf;
    const ctx_r6 = i0.ɵɵnextContext();
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate1(" ", i0.ɵɵpipeBind1(2, 11, "path-view:opponents-in-round"), " ");
    i0.ɵɵadvance(3);
    i0.ɵɵproperty("contestant", ctx_r6.participants[ctx_r6.opponentId])("league", ctx_r6.league);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", opponent_r1.entry.photo);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", opponent_r1.entry.logo);
    i0.ɵɵadvance(3);
    i0.ɵɵproperty("country", opponent_r1.entry.region);
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("contestant", ctx_r6.participants[ctx_r6.opponentId])("league", ctx_r6.league);
    i0.ɵɵadvance(2);
    i0.ɵɵtextInterpolate1(" ", ctx_r6.participants[ctx_r6.opponentId] == null ? null : ctx_r6.participants[ctx_r6.opponentId].name, " ");
    i0.ɵɵadvance(3);
    i0.ɵɵtextInterpolate1(" ", i0.ɵɵpipeBind1(17, 13, "path-view:last-matches"), " ");
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("ngForOf", ctx_r6.displayMatches);
} }
function MatchOpponentComponent_div_0_ng_template_6_li_15_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "li");
    i0.ɵɵelement(1, "div", 24);
    i0.ɵɵelementEnd();
} }
function MatchOpponentComponent_div_0_ng_template_6_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 23);
    i0.ɵɵelementStart(1, "div", 6);
    i0.ɵɵelement(2, "div", 24);
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(3, "div", 7);
    i0.ɵɵelementStart(4, "div", 25);
    i0.ɵɵelement(5, "div", 26);
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(6, "div", 10);
    i0.ɵɵelementStart(7, "div", 11);
    i0.ɵɵelement(8, "country-flag", 12);
    i0.ɵɵelementStart(9, "div", 13);
    i0.ɵɵelement(10, "div", 27);
    i0.ɵɵelementEnd();
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(11, "ul", 15);
    i0.ɵɵelementStart(12, "li", 16);
    i0.ɵɵtext(13);
    i0.ɵɵpipe(14, "translate");
    i0.ɵɵelementEnd();
    i0.ɵɵtemplate(15, MatchOpponentComponent_div_0_ng_template_6_li_15_Template, 2, 0, "li", 28);
    i0.ɵɵelementEnd();
    i0.ɵɵelementEnd();
    i0.ɵɵelementEnd();
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r8 = i0.ɵɵnextContext(2);
    i0.ɵɵadvance(5);
    i0.ɵɵstyleProp("height", 100, "%");
    i0.ɵɵadvance(3);
    i0.ɵɵproperty("country", null);
    i0.ɵɵadvance(1);
    i0.ɵɵstyleProp("width", 100, "px");
    i0.ɵɵadvance(4);
    i0.ɵɵtextInterpolate1(" ", i0.ɵɵpipeBind1(14, 7, "path-view:last-matches"), " ");
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("ngForOf", ctx_r8.displayMatches);
} }
function MatchOpponentComponent_div_0_ng_template_8_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "inline-error");
    i0.ɵɵtext(1);
    i0.ɵɵpipe(2, "translate");
    i0.ɵɵelementEnd();
} if (rf & 2) {
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate1(" ", i0.ɵɵpipeBind1(2, 1, "commons:data-fetch-error"), " ");
} }
function MatchOpponentComponent_div_0_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 1);
    i0.ɵɵtemplate(1, MatchOpponentComponent_div_0_ng_container_1_Template, 1, 0, "ng-container", 2);
    i0.ɵɵtemplate(2, MatchOpponentComponent_div_0_ng_container_2_Template, 1, 0, "ng-container", 2);
    i0.ɵɵtemplate(3, MatchOpponentComponent_div_0_ng_container_3_Template, 1, 0, "ng-container", 2);
    i0.ɵɵtemplate(4, MatchOpponentComponent_div_0_ng_template_4_Template, 19, 15, "ng-template", null, 3, i0.ɵɵtemplateRefExtractor);
    i0.ɵɵtemplate(6, MatchOpponentComponent_div_0_ng_template_6_Template, 16, 9, "ng-template", null, 4, i0.ɵɵtemplateRefExtractor);
    i0.ɵɵtemplate(8, MatchOpponentComponent_div_0_ng_template_8_Template, 3, 3, "ng-template", null, 5, i0.ɵɵtemplateRefExtractor);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const opponent_r1 = ctx.ngIf;
    const _r5 = i0.ɵɵreference(5);
    const _r7 = i0.ɵɵreference(7);
    const _r9 = i0.ɵɵreference(9);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", opponent_r1.entry)("ngIfThen", _r5);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", opponent_r1.loading && !opponent_r1.entry)("ngIfThen", _r7);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", opponent_r1.error && !opponent_r1.loading)("ngIfThen", _r9);
} }
class MatchOpponentComponent {
    constructor(matchService, store, configMaxDisplay) {
        this.matchService = matchService;
        this.store = store;
        this.configMaxDisplay = configMaxDisplay;
    }
    ngOnChanges(changes) {
        if (changes.opponentId) {
            if (isTeam(this.league)) {
                this.store.dispatch(new LoadTeamOnceAction(this.opponentId));
                this.opponent$ = this.store.select(getTeam(this.opponentId));
            }
            else {
                this.store.dispatch(new LoadPlayerOnceAction(this.opponentId));
                this.opponent$ = this.store.select(getPlayer(this.opponentId));
            }
        }
        this.displayAmount = this.configMaxDisplay < this.opponentMatches.length ? this.configMaxDisplay - 1 : this.configMaxDisplay;
        this.displayMatches = this.buildMatches(this.opponentMatches.slice(0, this.displayAmount));
    }
    buildMatches(matches) {
        return matches.map(match => {
            const contestantIndex = this.getContestantIndex(this.opponentId, match);
            const versusIndex = this.getOtherContestantIndex(this.opponentId, match);
            const isWin = match.participants[contestantIndex].place === 1;
            const versusId = match.participants[versusIndex].id;
            return {
                isWin: isWin,
                isLoss: !isWin,
                isDefaultWin: isWin && !versusId,
                versusContestant: versusId,
            };
        });
    }
    getContestantIndex(id, match) {
        return this.matchService.getContestantIndexOfMatch(match, id);
    }
    getOtherContestantIndex(id, match) {
        const contestantIndex = this.getContestantIndex(id, match);
        return contestantIndex ? 0 : 1;
    }
}
MatchOpponentComponent.ɵfac = function MatchOpponentComponent_Factory(t) { return new (t || MatchOpponentComponent)(i0.ɵɵdirectiveInject(MatchService), i0.ɵɵdirectiveInject(i1$2.Store), i0.ɵɵdirectiveInject(MATCH_CONTESTANTS_MAX_DISPLAY)); };
MatchOpponentComponent.ɵcmp = i0.ɵɵdefineComponent({ type: MatchOpponentComponent, selectors: [["path-view-match-opponent"]], inputs: { opponentId: "opponentId", opponentMatches: "opponentMatches", league: "league", participants: "participants" }, features: [i0.ɵɵNgOnChangesFeature], decls: 2, vars: 3, consts: [["class", "match-contestants match-contestants--opponent", 4, "ngIf"], [1, "match-contestants", "match-contestants--opponent"], [4, "ngIf", "ngIfThen"], ["ready", ""], ["loading", ""], ["error", ""], [1, "match-contestants__heading"], [1, "match-contestants__opponent"], [3, "contestant", "league"], ["class", "match-contestants__opponent__photo", 3, "src", 4, "ngIf"], [1, "match-contestants__opponent__info"], [1, "match-contestants__opponent__user"], [3, "country"], [1, "match-contestants__contestant-list__contestant"], [1, "match-contestants__opponent__name"], [1, "match-contestants__opponent__matches"], [1, "match-contestants__opponent__matches__title"], ["class", "match-contestants__opponent__match", 4, "ngFor", "ngForOf"], [1, "match-contestants__opponent__photo", 3, "src"], [1, "match-contestants__opponent__match"], [1, "match-contestants__opponent__match__status", 3, "ngClass"], [4, "ngIf"], [1, "contestant-name", "contestant-name--long"], [1, "c-skeleton"], [1, "c-skeleton__text", "c-skeleton__text--small"], [1, "match-contestants__opponent__photo", 2, "width", "79px", "height", "79px"], [1, "c-skeleton__figure"], [1, "match-contestants__opponent__name", "c-skeleton__text", "c-skeleton__text--full"], [4, "ngFor", "ngForOf"]], template: function MatchOpponentComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵtemplate(0, MatchOpponentComponent_div_0_Template, 10, 6, "div", 0);
        i0.ɵɵpipe(1, "async");
    } if (rf & 2) {
        i0.ɵɵproperty("ngIf", i0.ɵɵpipeBind1(1, 1, ctx.opponent$));
    } }, directives: [i1$1.NgIf, ContestantLinkComponent, i1.CountryFlagComponent, i1$1.NgForOf, i1$1.NgClass, i1.InlineErrorComponent], pipes: [i1$1.AsyncPipe, i3.TranslatePipe], encapsulation: 2, changeDetection: 0 });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(MatchOpponentComponent, [{
        type: Component,
        args: [{
                selector: "path-view-match-opponent",
                templateUrl: "./match-opponent.component.html",
                encapsulation: ViewEncapsulation.Emulated,
                changeDetection: ChangeDetectionStrategy.OnPush,
            }]
    }], function () { return [{ type: MatchService }, { type: i1$2.Store }, { type: undefined, decorators: [{
                type: Inject,
                args: [MATCH_CONTESTANTS_MAX_DISPLAY]
            }] }]; }, { opponentId: [{
            type: Input
        }], opponentMatches: [{
            type: Input
        }], league: [{
            type: Input
        }], participants: [{
            type: Input
        }] }); })();

class MatchStatusSkeletonComponent {
}
MatchStatusSkeletonComponent.ɵfac = function MatchStatusSkeletonComponent_Factory(t) { return new (t || MatchStatusSkeletonComponent)(); };
MatchStatusSkeletonComponent.ɵcmp = i0.ɵɵdefineComponent({ type: MatchStatusSkeletonComponent, selectors: [["path-view-match-status-skeleton"]], decls: 3, vars: 0, consts: [[1, "c-skeleton", "match-status"], [1, "match-status__indicator", "is-following"], ["icon", "dot", 1, "match-status__indicator__icon"]], template: function MatchStatusSkeletonComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵelementStart(0, "div", 0);
        i0.ɵɵelementStart(1, "div", 1);
        i0.ɵɵelement(2, "svg-icon", 2);
        i0.ɵɵelementEnd();
        i0.ɵɵelementEnd();
    } }, directives: [i1.SvgIconComponent], encapsulation: 2, changeDetection: 0 });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(MatchStatusSkeletonComponent, [{
        type: Component,
        args: [{
                selector: "path-view-match-status-skeleton",
                templateUrl: "./match-status-skeleton.component.html",
                encapsulation: ViewEncapsulation.Emulated,
                changeDetection: ChangeDetectionStrategy.OnPush,
            }]
    }], null, null); })();

class MatchResultPlayerSkeletonComponent {
}
MatchResultPlayerSkeletonComponent.ɵfac = function MatchResultPlayerSkeletonComponent_Factory(t) { return new (t || MatchResultPlayerSkeletonComponent)(); };
MatchResultPlayerSkeletonComponent.ɵcmp = i0.ɵɵdefineComponent({ type: MatchResultPlayerSkeletonComponent, selectors: [["path-view-match-result-player-skeleton"]], decls: 4, vars: 1, consts: [[1, "quest-match__result__player", "c-skeleton"], [1, "quest-match__result__player__flag", 3, "country"], [1, "quest-match__result__player__name"], [1, "c-skeleton__text"]], template: function MatchResultPlayerSkeletonComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵelementStart(0, "div", 0);
        i0.ɵɵelement(1, "country-flag", 1);
        i0.ɵɵelementStart(2, "div", 2);
        i0.ɵɵelement(3, "div", 3);
        i0.ɵɵelementEnd();
        i0.ɵɵelementEnd();
    } if (rf & 2) {
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("country", null);
    } }, directives: [i1.CountryFlagComponent], encapsulation: 2, changeDetection: 0 });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(MatchResultPlayerSkeletonComponent, [{
        type: Component,
        args: [{
                selector: "path-view-match-result-player-skeleton",
                templateUrl: "./match-result-player-skeleton.component.html",
                encapsulation: ViewEncapsulation.Emulated,
                changeDetection: ChangeDetectionStrategy.OnPush,
            }]
    }], null, null); })();

class MatchSkeletonComponent {
}
MatchSkeletonComponent.ɵfac = function MatchSkeletonComponent_Factory(t) { return new (t || MatchSkeletonComponent)(); };
MatchSkeletonComponent.ɵcmp = i0.ɵɵdefineComponent({ type: MatchSkeletonComponent, selectors: [["path-view-match-skeleton"]], decls: 7, vars: 0, consts: [[1, "c-skeleton", "quest-match"], [1, "quest-match__heading", "c-skeleton__text", "c-skeleton__text--small"], [1, "quest-match__result"], [1, "quest-match__result__wrapper"], [1, "quest-match__result__vs"]], template: function MatchSkeletonComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵelementStart(0, "article", 0);
        i0.ɵɵelement(1, "div", 1);
        i0.ɵɵelementStart(2, "div", 2);
        i0.ɵɵelement(3, "path-view-match-result-player-skeleton", 3);
        i0.ɵɵelement(4, "path-view-match-result-player-skeleton", 3);
        i0.ɵɵelementStart(5, "span", 4);
        i0.ɵɵtext(6, "VS");
        i0.ɵɵelementEnd();
        i0.ɵɵelementEnd();
        i0.ɵɵelementEnd();
    } }, directives: [MatchResultPlayerSkeletonComponent], encapsulation: 2, changeDetection: 0 });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(MatchSkeletonComponent, [{
        type: Component,
        args: [{
                selector: "path-view-match-skeleton",
                templateUrl: "./match-skeleton.component.html",
                encapsulation: ViewEncapsulation.Emulated,
                changeDetection: ChangeDetectionStrategy.OnPush,
            }]
    }], null, null); })();

class ContestantListItemSkeletonComponent {
}
ContestantListItemSkeletonComponent.ɵfac = function ContestantListItemSkeletonComponent_Factory(t) { return new (t || ContestantListItemSkeletonComponent)(); };
ContestantListItemSkeletonComponent.ɵcmp = i0.ɵɵdefineComponent({ type: ContestantListItemSkeletonComponent, selectors: [["path-view-contestant-list-item-skeleton"]], decls: 3, vars: 1, consts: [[1, "c-skeleton", "match-contestants__contestant-list__item"], [3, "country"], [1, "match-contestants__contestant-list__contestant", "c-skeleton__text"]], template: function ContestantListItemSkeletonComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵelementStart(0, "div", 0);
        i0.ɵɵelement(1, "country-flag", 1);
        i0.ɵɵelement(2, "div", 2);
        i0.ɵɵelementEnd();
    } if (rf & 2) {
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("country", null);
    } }, directives: [i1.CountryFlagComponent], encapsulation: 2, changeDetection: 0 });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(ContestantListItemSkeletonComponent, [{
        type: Component,
        args: [{
                selector: "path-view-contestant-list-item-skeleton",
                templateUrl: "./contestant-list-item-skeleton.component.html",
                encapsulation: ViewEncapsulation.Emulated,
                changeDetection: ChangeDetectionStrategy.OnPush,
            }]
    }], null, null); })();

function MatchContestantsSkeletonComponent_path_view_contestant_list_item_skeleton_3_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelement(0, "path-view-contestant-list-item-skeleton");
} }
class MatchContestantsSkeletonComponent {
    constructor() {
        this.contestants = new Array(8);
    }
}
MatchContestantsSkeletonComponent.ɵfac = function MatchContestantsSkeletonComponent_Factory(t) { return new (t || MatchContestantsSkeletonComponent)(); };
MatchContestantsSkeletonComponent.ɵcmp = i0.ɵɵdefineComponent({ type: MatchContestantsSkeletonComponent, selectors: [["path-view-match-contestants-skeleton"]], decls: 4, vars: 1, consts: [[1, "c-skeleton", "match-contestants", "match-contestants--droped-out"], [1, "match-contestants__heading", "c-skeleton__text"], [1, "match-contestants__contestant-list"], [4, "ngFor", "ngForOf"]], template: function MatchContestantsSkeletonComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵelementStart(0, "div", 0);
        i0.ɵɵelement(1, "div", 1);
        i0.ɵɵelementStart(2, "ul", 2);
        i0.ɵɵtemplate(3, MatchContestantsSkeletonComponent_path_view_contestant_list_item_skeleton_3_Template, 1, 0, "path-view-contestant-list-item-skeleton", 3);
        i0.ɵɵelementEnd();
        i0.ɵɵelementEnd();
    } if (rf & 2) {
        i0.ɵɵadvance(3);
        i0.ɵɵproperty("ngForOf", ctx.contestants);
    } }, directives: [i1$1.NgForOf, ContestantListItemSkeletonComponent], encapsulation: 2, changeDetection: 0 });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(MatchContestantsSkeletonComponent, [{
        type: Component,
        args: [{
                selector: "path-view-match-contestants-skeleton",
                templateUrl: "./match-contestants-skeleton.component.html",
                encapsulation: ViewEncapsulation.Emulated,
                changeDetection: ChangeDetectionStrategy.OnPush,
            }]
    }], null, null); })();

class RoundBoxSkeletonComponent {
}
RoundBoxSkeletonComponent.ɵfac = function RoundBoxSkeletonComponent_Factory(t) { return new (t || RoundBoxSkeletonComponent)(); };
RoundBoxSkeletonComponent.ɵcmp = i0.ɵɵdefineComponent({ type: RoundBoxSkeletonComponent, selectors: [["path-view-round-box-skeleton"]], decls: 8, vars: 0, consts: [[1, "round-box"], [1, "flex-stretch"], [1, "flex-center--vertical"], [1, "flex-auto"], [1, "round-message"]], template: function RoundBoxSkeletonComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵelementStart(0, "article", 0);
        i0.ɵɵelementStart(1, "section", 1);
        i0.ɵɵelement(2, "path-view-match-status-skeleton", 2);
        i0.ɵɵelementEnd();
        i0.ɵɵelementStart(3, "section");
        i0.ɵɵelement(4, "path-view-match-skeleton");
        i0.ɵɵelementEnd();
        i0.ɵɵelementStart(5, "section", 3);
        i0.ɵɵelement(6, "path-view-match-contestants-skeleton");
        i0.ɵɵelementEnd();
        i0.ɵɵelementEnd();
        i0.ɵɵelement(7, "article", 4);
    } }, directives: [MatchStatusSkeletonComponent, MatchSkeletonComponent, MatchContestantsSkeletonComponent], encapsulation: 2, changeDetection: 0 });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(RoundBoxSkeletonComponent, [{
        type: Component,
        args: [{
                selector: "path-view-round-box-skeleton",
                templateUrl: "./round-box-skeleton.component.html",
                encapsulation: ViewEncapsulation.Emulated,
                changeDetection: ChangeDetectionStrategy.OnPush,
            }]
    }], null, null); })();

class PathViewSkeletonComponent {
}
PathViewSkeletonComponent.ɵfac = function PathViewSkeletonComponent_Factory(t) { return new (t || PathViewSkeletonComponent)(); };
PathViewSkeletonComponent.ɵcmp = i0.ɵɵdefineComponent({ type: PathViewSkeletonComponent, selectors: [["path-view-path-view-skeleton"]], decls: 10, vars: 5, consts: [[1, "c-skeleton", "c-path-view"], [1, "cup-page__header"], [1, "c-participant-switcher"], [1, "c-participant-switcher__name"], [1, "c-skeleton__text", "c-skeleton__text--full"]], template: function PathViewSkeletonComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵelementStart(0, "div", 0);
        i0.ɵɵelementStart(1, "div", 1);
        i0.ɵɵelementStart(2, "div", 2);
        i0.ɵɵtext(3);
        i0.ɵɵpipe(4, "translate");
        i0.ɵɵelementStart(5, "button", 3);
        i0.ɵɵelement(6, "div", 4);
        i0.ɵɵelementEnd();
        i0.ɵɵelementEnd();
        i0.ɵɵelementEnd();
        i0.ɵɵelement(7, "path-view-round-box-skeleton");
        i0.ɵɵelement(8, "path-view-round-box-skeleton");
        i0.ɵɵelement(9, "path-view-round-box-skeleton");
        i0.ɵɵelementEnd();
    } if (rf & 2) {
        i0.ɵɵadvance(3);
        i0.ɵɵtextInterpolate1(" ", i0.ɵɵpipeBind1(4, 3, "participant-switcher:viewing-as"), " ");
        i0.ɵɵadvance(2);
        i0.ɵɵstyleProp("width", 75, "px");
    } }, directives: [RoundBoxSkeletonComponent], pipes: [i3.TranslatePipe], encapsulation: 2, changeDetection: 0 });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(PathViewSkeletonComponent, [{
        type: Component,
        args: [{
                selector: "path-view-path-view-skeleton",
                templateUrl: "./path-view-skeleton.component.html",
                encapsulation: ViewEncapsulation.Emulated,
                changeDetection: ChangeDetectionStrategy.OnPush,
            }]
    }], null, null); })();

const _c0 = function (a0, a1, a2) { return { player: a0, team: a1, type: a2 }; };
function StatsBoxComponent_ng_container_0_article_1_section_1_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "section", 5);
    i0.ɵɵelementStart(1, "div", 6);
    i0.ɵɵelementStart(2, "span", 7);
    i0.ɵɵtext(3);
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(4, "span", 8);
    i0.ɵɵtext(5);
    i0.ɵɵpipe(6, "translate");
    i0.ɵɵelementEnd();
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(7, "div", 6);
    i0.ɵɵelementStart(8, "span", 7);
    i0.ɵɵtext(9);
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(10, "span", 8);
    i0.ɵɵtext(11);
    i0.ɵɵpipe(12, "translate");
    i0.ɵɵelementEnd();
    i0.ɵɵelementEnd();
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const participants_r6 = ctx.ngIf;
    const data_r3 = i0.ɵɵnextContext().ngIf;
    i0.ɵɵadvance(3);
    i0.ɵɵtextInterpolate1(" ", participants_r6.count - participants_r6.eliminated, " ");
    i0.ɵɵadvance(2);
    i0.ɵɵtextInterpolate1(" ", i0.ɵɵpipeBind2(6, 4, "path-view:contestants-left", i0.ɵɵpureFunction3(10, _c0, !data_r3.isTeam, data_r3.isTeam, data_r3.isTeam ? "team" : "player")), " ");
    i0.ɵɵadvance(4);
    i0.ɵɵtextInterpolate1(" ", participants_r6.eliminated, " ");
    i0.ɵɵadvance(2);
    i0.ɵɵtextInterpolate1(" ", i0.ɵɵpipeBind2(12, 7, "path-view:contestants-eliminated", i0.ɵɵpureFunction3(14, _c0, !data_r3.isTeam, data_r3.isTeam, data_r3.isTeam ? "team" : "player")), " ");
} }
function StatsBoxComponent_ng_container_0_article_1_section_4_h2_1_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "h2", 13);
    i0.ɵɵtext(1);
    i0.ɵɵpipe(2, "translate");
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const data_r3 = i0.ɵɵnextContext(2).ngIf;
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate1(" ", i0.ɵɵpipeBind2(2, 1, "path-view:top-contestants-league", i0.ɵɵpureFunction3(4, _c0, !data_r3.isTeam, data_r3.isTeam, data_r3.isTeam ? "team" : "player")), " ");
} }
function StatsBoxComponent_ng_container_0_article_1_section_4_h2_2_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "h2", 13);
    i0.ɵɵtext(1);
    i0.ɵɵpipe(2, "translate");
    i0.ɵɵelementEnd();
} if (rf & 2) {
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate1(" ", i0.ɵɵpipeBind1(2, 1, "path-view:contestants-not-seeded"), " ");
} }
function StatsBoxComponent_ng_container_0_article_1_section_4_ul_3_li_1_button_2_Template(rf, ctx) { if (rf & 1) {
    const _r20 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "button", 22);
    i0.ɵɵlistener("click", function StatsBoxComponent_ng_container_0_article_1_section_4_ul_3_li_1_button_2_Template_button_click_0_listener() { i0.ɵɵrestoreView(_r20); const contestant_r15 = i0.ɵɵnextContext().$implicit; const ctx_r18 = i0.ɵɵnextContext(5); return ctx_r18.selectParticipant(contestant_r15.id); });
    i0.ɵɵelement(1, "svg-icon", 23);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("icon", "eye");
} }
function StatsBoxComponent_ng_container_0_article_1_section_4_ul_3_li_1_premium_badge_7_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelement(0, "premium-badge");
} }
const _c1 = function (a0) { return { "is-eliminated": a0 }; };
function StatsBoxComponent_ng_container_0_article_1_section_4_ul_3_li_1_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "li", 16);
    i0.ɵɵelementStart(1, "span", 17);
    i0.ɵɵtemplate(2, StatsBoxComponent_ng_container_0_article_1_section_4_ul_3_li_1_button_2_Template, 2, 1, "button", 18);
    i0.ɵɵelementEnd();
    i0.ɵɵelement(3, "country-flag", 19);
    i0.ɵɵelementStart(4, "path-view-contestant-link", 20);
    i0.ɵɵelementStart(5, "span", 21);
    i0.ɵɵtext(6);
    i0.ɵɵelementEnd();
    i0.ɵɵelementEnd();
    i0.ɵɵtemplate(7, StatsBoxComponent_ng_container_0_article_1_section_4_ul_3_li_1_premium_badge_7_Template, 1, 0, "premium-badge", 0);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const contestant_r15 = ctx.$implicit;
    const data_r3 = i0.ɵɵnextContext(3).ngIf;
    const players_r1 = i0.ɵɵnextContext().ngIf;
    i0.ɵɵproperty("ngClass", i0.ɵɵpureFunction1(7, _c1, contestant_r15.isEliminated));
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("ngIf", !contestant_r15.isSelected);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("country", contestant_r15.region);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("contestant", contestant_r15)("league", data_r3.league == null ? null : data_r3.league.entry);
    i0.ɵɵadvance(2);
    i0.ɵɵtextInterpolate1(" ", contestant_r15.name, " ");
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", players_r1[contestant_r15.id] == null ? null : players_r1[contestant_r15.id].premium);
} }
function StatsBoxComponent_ng_container_0_article_1_section_4_ul_3_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "ul", 14);
    i0.ɵɵtemplate(1, StatsBoxComponent_ng_container_0_article_1_section_4_ul_3_li_1_Template, 8, 9, "li", 15);
    i0.ɵɵpipe(2, "async");
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r11 = i0.ɵɵnextContext(4);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngForOf", i0.ɵɵpipeBind1(2, 1, ctx_r11.contestants$));
} }
function StatsBoxComponent_ng_container_0_article_1_section_4_span_4_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "span", 24);
    i0.ɵɵtext(1);
    i0.ɵɵpipe(2, "translate");
    i0.ɵɵelementEnd();
} if (rf & 2) {
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate1(" ", i0.ɵɵpipeBind1(2, 1, "path-view:seedings-disclaimer"), " ");
} }
function StatsBoxComponent_ng_container_0_article_1_section_4_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "section", 9);
    i0.ɵɵtemplate(1, StatsBoxComponent_ng_container_0_article_1_section_4_h2_1_Template, 3, 8, "h2", 10);
    i0.ɵɵtemplate(2, StatsBoxComponent_ng_container_0_article_1_section_4_h2_2_Template, 3, 3, "h2", 10);
    i0.ɵɵtemplate(3, StatsBoxComponent_ng_container_0_article_1_section_4_ul_3_Template, 3, 3, "ul", 11);
    i0.ɵɵtemplate(4, StatsBoxComponent_ng_container_0_article_1_section_4_span_4_Template, 3, 3, "span", 12);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const participantsCount_r8 = ctx.ngIf;
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", participantsCount_r8 > 0);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", participantsCount_r8 == 0);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", participantsCount_r8 > 0);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", participantsCount_r8);
} }
const _c2 = function (a0, a1) { return { count: a0, eliminated: a1 }; };
function StatsBoxComponent_ng_container_0_article_1_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "article", 2);
    i0.ɵɵtemplate(1, StatsBoxComponent_ng_container_0_article_1_section_1_Template, 13, 18, "section", 3);
    i0.ɵɵpipe(2, "async");
    i0.ɵɵpipe(3, "async");
    i0.ɵɵtemplate(4, StatsBoxComponent_ng_container_0_article_1_section_4_Template, 5, 4, "section", 4);
    i0.ɵɵpipe(5, "async");
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r2 = i0.ɵɵnextContext(2);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", i0.ɵɵpureFunction2(8, _c2, i0.ɵɵpipeBind1(2, 2, ctx_r2.participantsCount$), i0.ɵɵpipeBind1(3, 4, ctx_r2.eliminatedParticipantsCount$)));
    i0.ɵɵadvance(3);
    i0.ɵɵproperty("ngIf", i0.ɵɵpipeBind1(5, 6, ctx_r2.participantsCount$));
} }
const _c3 = function (a0, a1) { return { league: a0, isTeam: a1 }; };
function StatsBoxComponent_ng_container_0_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵtemplate(1, StatsBoxComponent_ng_container_0_article_1_Template, 6, 11, "article", 1);
    i0.ɵɵpipe(2, "async");
    i0.ɵɵpipe(3, "async");
    i0.ɵɵelementContainerEnd();
} if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", i0.ɵɵpureFunction2(5, _c3, i0.ɵɵpipeBind1(2, 1, ctx_r0.league$), i0.ɵɵpipeBind1(3, 3, ctx_r0.isTeamLeague$)));
} }
const STATS_BOX_MAX_DISPLAY = new InjectionToken("path-view.stats-box.max-display");
class StatsBoxComponent {
    constructor(store, matchService, configMaxDisplay) {
        this.store = store;
        this.matchService = matchService;
        this.configMaxDisplay = configMaxDisplay;
    }
    ngOnInit() {
        this.store.dispatch(new LoadLeagueAction(this.leagueId));
        this.league$ = this.store.select(getLeague(this.leagueId)).pipe(filter$1(entryAvailable));
        this.isTeamLeague$ = this.league$.pipe(map$1(league => isTeam(league.entry)));
        this.store.dispatch(new LoadParticipantsOnceAction(this.leagueId));
        this.participants$ = this.store.select(getLeagueParticipants(this.leagueId)).pipe(filter$1(exists), map$1(entry => (Object.assign(Object.assign({}, entry), { entry: entry.entry ? keyBy(entry.entry.filter(isCheckedIn), p => p.id) : entry.entry }))));
        this.store.dispatch(new LoadLeagueResultsOnceAction(this.leagueId));
        this.matches$ = this.store.select(getLeagueResults(this.leagueId)).pipe(filter$1(entryAvailable));
        const selectedParticipant$ = this.store.pipe(getSelectedParticipant(this.leagueId));
        const eliminatedParticipants$ = combineLatest(this.matches$, this.participants$).pipe(map$1(([matches, participants]) => {
            return this.matchService.getEliminatedContestants(matches.entry, map(participants.entry, p => p));
        }));
        const topSeedParticipants$ = this.participants$.pipe(map$1(participants => sortBy(participants.entry, participant => participant.seed)), map$1(participants => participants.slice(0, this.configMaxDisplay)));
        this.players$ = this.league$.pipe(filter$1(league => !isTeam(league.entry)), switchMap(() => topSeedParticipants$), switchMap(participants => {
            const playerIds = participants.map(({ id }) => id);
            for (let i = 0; i < Math.ceil(playerIds.length / 100); i++) {
                const slicedPlayerIds = playerIds.slice(100 * i, 100 * (i + 1));
                this.store.dispatch(new LoadPlayersOnceAction(slicedPlayerIds));
            }
            return this.store.select(getPlayers(playerIds));
        }), playersToDictionary());
        this.eliminatedParticipantsCount$ = eliminatedParticipants$.pipe(map$1(participants => participants.length));
        this.contestants$ = combineLatest(topSeedParticipants$, eliminatedParticipants$, selectedParticipant$).pipe(map$1(([participants, eliminatedParticipants, selectedParticipant]) => {
            return participants.map(participant => (Object.assign(Object.assign({}, participant), { isEliminated: eliminatedParticipants.indexOf(participant.id) !== -1, isSelected: participant.id === selectedParticipant })));
        }));
        this.participantsCount$ = this.participants$.pipe(map$1(participants => Object.keys(participants.entry).length));
    }
    selectParticipant(id) {
        this.store.dispatch(new SelectParticipantAction(this.leagueId, id));
    }
}
StatsBoxComponent.ɵfac = function StatsBoxComponent_Factory(t) { return new (t || StatsBoxComponent)(i0.ɵɵdirectiveInject(i1$2.Store), i0.ɵɵdirectiveInject(MatchService), i0.ɵɵdirectiveInject(STATS_BOX_MAX_DISPLAY)); };
StatsBoxComponent.ɵcmp = i0.ɵɵdefineComponent({ type: StatsBoxComponent, selectors: [["path-view-stats-box"]], inputs: { leagueId: "leagueId" }, decls: 2, vars: 3, consts: [[4, "ngIf"], ["class", "stats-box", 4, "ngIf"], [1, "stats-box"], ["class", "stats-box__stats", 4, "ngIf"], ["class", "stats-box__contestants", 4, "ngIf"], [1, "stats-box__stats"], [1, "stats-box__stat"], [1, "stats-box__stat__count"], [1, "stats-box__stat__description"], [1, "stats-box__contestants"], ["class", "stats-box__heading", 4, "ngIf"], ["class", "stats-box__contestant-list", 4, "ngIf"], ["class", "stats-box__disclaimer", 4, "ngIf"], [1, "stats-box__heading"], [1, "stats-box__contestant-list"], ["class", "stats-box__contestant-list__item", 3, "ngClass", 4, "ngFor", "ngForOf"], [1, "stats-box__contestant-list__item", 3, "ngClass"], [1, "contestant__pov-switch-container"], ["class", "contestant__pov-switch", 3, "click", 4, "ngIf"], [3, "country"], [3, "contestant", "league"], [1, "stats-box__contestant-list__contestant", "contestant-name"], [1, "contestant__pov-switch", 3, "click"], [1, "contestant__eye", "contestant__eye--small", 3, "icon"], [1, "stats-box__disclaimer"]], template: function StatsBoxComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵtemplate(0, StatsBoxComponent_ng_container_0_Template, 4, 8, "ng-container", 0);
        i0.ɵɵpipe(1, "async");
    } if (rf & 2) {
        i0.ɵɵproperty("ngIf", i0.ɵɵpipeBind1(1, 1, ctx.players$));
    } }, directives: [i1$1.NgIf, i1$1.NgForOf, i1$1.NgClass, i1.CountryFlagComponent, ContestantLinkComponent, i1.SvgIconComponent, i4.PremiumBadgeComponent], pipes: [i1$1.AsyncPipe, i3.TranslatePipe], encapsulation: 2, changeDetection: 0 });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(StatsBoxComponent, [{
        type: Component,
        args: [{
                selector: "path-view-stats-box",
                templateUrl: "./stats-box.component.html",
                encapsulation: ViewEncapsulation.Emulated,
                changeDetection: ChangeDetectionStrategy.OnPush,
            }]
    }], function () { return [{ type: i1$2.Store }, { type: MatchService }, { type: undefined, decorators: [{
                type: Inject,
                args: [STATS_BOX_MAX_DISPLAY]
            }] }]; }, { leagueId: [{
            type: Input
        }] }); })();

function StatsBoxSkeletonComponent_li_16_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "li", 13);
    i0.ɵɵelement(1, "country-flag", 14);
    i0.ɵɵelement(2, "div", 15);
    i0.ɵɵelementEnd();
} }
class StatsBoxSkeletonComponent {
    constructor() {
        this.contestants = new Array(16);
    }
}
StatsBoxSkeletonComponent.ɵfac = function StatsBoxSkeletonComponent_Factory(t) { return new (t || StatsBoxSkeletonComponent)(); };
StatsBoxSkeletonComponent.ɵcmp = i0.ɵɵdefineComponent({ type: StatsBoxSkeletonComponent, selectors: [["path-view-stats-box-skeleton"]], decls: 19, vars: 3, consts: [[1, "stats-box", "c-skeleton"], [1, "stats-box__stats"], [1, "stats-box__stat"], [1, "stats-box__stat__count"], [1, "c-skeleton__text"], [1, "stats-box__stat__description"], [1, "c-skeleton__text", "c-skeleton__text--small"], [1, "stats-box__contestants"], [1, "stats-box__heading"], [1, "stats-box__contestant-list"], ["class", "stats-box__contestant-list__item", 4, "ngFor", "ngForOf"], [1, "stats-box__disclaimer"], [1, "c-skeleton__text", "c-skeleton__text--tiny", "c-skeleton__text--inline"], [1, "stats-box__contestant-list__item"], ["country", "null"], [1, "stats-box__contestant-list__contestant", "c-skeleton__text"]], template: function StatsBoxSkeletonComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵelementStart(0, "article", 0);
        i0.ɵɵelementStart(1, "section", 1);
        i0.ɵɵelementStart(2, "div", 2);
        i0.ɵɵelementStart(3, "div", 3);
        i0.ɵɵelement(4, "div", 4);
        i0.ɵɵelementEnd();
        i0.ɵɵelementStart(5, "div", 5);
        i0.ɵɵelement(6, "div", 4);
        i0.ɵɵelementEnd();
        i0.ɵɵelementEnd();
        i0.ɵɵelementStart(7, "div", 2);
        i0.ɵɵelementStart(8, "div", 3);
        i0.ɵɵelement(9, "div", 6);
        i0.ɵɵelementEnd();
        i0.ɵɵelementStart(10, "div", 5);
        i0.ɵɵelement(11, "div", 6);
        i0.ɵɵelementEnd();
        i0.ɵɵelementEnd();
        i0.ɵɵelementEnd();
        i0.ɵɵelementStart(12, "section", 7);
        i0.ɵɵelementStart(13, "h2", 8);
        i0.ɵɵelement(14, "div", 4);
        i0.ɵɵelementEnd();
        i0.ɵɵelementStart(15, "ul", 9);
        i0.ɵɵtemplate(16, StatsBoxSkeletonComponent_li_16_Template, 3, 0, "li", 10);
        i0.ɵɵelementEnd();
        i0.ɵɵelementStart(17, "div", 11);
        i0.ɵɵelement(18, "div", 12);
        i0.ɵɵelementEnd();
        i0.ɵɵelementEnd();
        i0.ɵɵelementEnd();
    } if (rf & 2) {
        i0.ɵɵadvance(1);
        i0.ɵɵstyleProp("width", 120, "px");
        i0.ɵɵadvance(15);
        i0.ɵɵproperty("ngForOf", ctx.contestants);
    } }, directives: [i1$1.NgForOf, i1.CountryFlagComponent], encapsulation: 2, changeDetection: 0 });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(StatsBoxSkeletonComponent, [{
        type: Component,
        args: [{
                selector: "path-view-stats-box-skeleton",
                templateUrl: "./stats-box-skeleton.component.html",
                encapsulation: ViewEncapsulation.Emulated,
                changeDetection: ChangeDetectionStrategy.OnPush,
            }]
    }], null, null); })();

/* tslint:disable */
const crownIcon = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
        <path d="M16.34,11.38L12,5.77,7.61,11.38,2,5.77,3.25,18.24H20.71L22,5.77Zm0,0"></path>
    </svg>
`;

/* tslint:disable */
const skullIcon = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
        <path class="skull-half skull-half--left" d="M7,12.62A1.87,1.87,0,1,1,8.88,14.5,1.87,1.87,0,0,1,7,12.62Zm4.37,5.62v1.87H10.13V18.25H8.88v1.87H7.62V17.51L7,17A7.49,7.49,0,0,1,12,3.87V2A9.36,9.36,0,0,0,5.75,18.35v2.4A1.25,1.25,0,0,0,7,22h5V18.25H11.37Z"></path>
        <path class="skull-half skull-half--right" d="M13.25,12.62a1.87,1.87,0,1,1,1.87,1.87A1.87,1.87,0,0,1,13.25,12.62ZM12,2V3.87A7.49,7.49,0,0,1,17,17l-0.62.56v2.61H15.12V18.25H13.87v1.87H12.63V18.25H12V22h5a1.25,1.25,0,0,0,1.25-1.25v-2.4A9.36,9.36,0,0,0,12,2Z"></path>
    </svg>
`;

/* tslint:disable */
const dotIcon = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="10"></circle>
    </svg>
`;

/* tslint:disable */
const starIcon = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
        <path d="M21.91,9.59a1.91,1.91,0,0,0-1.68-1.32l-4.69-.35L13.77,3.59a1.92,1.92,0,0,0-3.54,0L8.46,7.92l-4.7.35A1.94,1.94,0,0,0,2.09,9.62a1.87,1.87,0,0,0,.6,2l3.57,3L5.15,19.22a1.88,1.88,0,0,0,.33,1.63A1.91,1.91,0,0,0,8,21.3l4-2.46,4,2.46a1.9,1.9,0,0,0,1,.28,2,2,0,0,0,1.51-.73,1.9,1.9,0,0,0,.36-1.62l-1.11-4.56,3.57-3a1.9,1.9,0,0,0,.59-2.05h0Zm-1.64.8-4.35,3.68,1.35,5.53a0.25,0.25,0,0,1,0,.23,0.28,0.28,0,0,1-.37.08l-4.85-3-4.85,3A0.28,0.28,0,0,1,7,20a0.29,0.29,0,0,1-.22-0.11,0.25,0.25,0,0,1,0-.23l1.35-5.54L3.74,10.39a0.25,0.25,0,0,1-.09-0.28A0.28,0.28,0,0,1,3.9,9.9l5.7-.42,2.14-5.26A0.28,0.28,0,0,1,12,4a0.25,0.25,0,0,1,.25.16L14.4,9.48,20.1,9.9a0.26,0.26,0,0,1,.25.19,0.27,0.27,0,0,1-.09.3h0Zm0,0"></path>
    </svg>
`;

/* tslint:disable */
const checkmarkIcon = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
        <path d="M21.89,5.67L19.23,3.33a0.41,0.41,0,0,0-.58,0L9,14.36,5.57,9.81a0.41,0.41,0,0,0-.27-0.16,0.41,0.41,0,0,0-.3.08L2.16,11.87a0.41,0.41,0,0,0-.08.58l6.36,8.41a0.41,0.41,0,0,0,.31.16h0a0.41,0.41,0,0,0,.31-0.14L21.93,6.25a0.41,0.41,0,0,0,0-.58h0Zm0,0"></path>
    </svg>
`;

/* tslint:disable */
const eyeIcon = `
    <svg class="contestant__eye-svg" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
        <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"></path>
    </svg>
`;

class PathViewComponentsModule {
    constructor(icons) {
        this.icons = icons;
        const icns = [
            { identifier: "crown", icon: crownIcon },
            { identifier: "skull", icon: skullIcon },
            { identifier: "dot", icon: dotIcon },
            { identifier: "star", icon: starIcon },
            { identifier: "checkmark", icon: checkmarkIcon },
            { identifier: "eye", icon: eyeIcon },
        ];
        icns.forEach(this.icons.addIcon.bind(this.icons));
    }
}
PathViewComponentsModule.ɵfac = function PathViewComponentsModule_Factory(t) { return new (t || PathViewComponentsModule)(i0.ɵɵinject(i1.SvgIconsRepository)); };
PathViewComponentsModule.ɵmod = i0.ɵɵdefineNgModule({ type: PathViewComponentsModule });
PathViewComponentsModule.ɵinj = i0.ɵɵdefineInjector({ imports: [[
            CommonModule,
            CommonsLocationModule,
            CommonsComponentModule,
            SvgIconsModule,
            TranslationModule,
            CupPageModule,
            CommonsErrorModule,
            PremiumModule,
        ]] });
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(PathViewComponentsModule, { declarations: [MatchLinkComponent,
        MatchComponent,
        RoundMessageComponent,
        ContestantLinkComponent,
        MatchStatusComponent,
        MatchContestantsComponent,
        RoundBoxComponent,
        PossibleOpponentsComponent,
        DroppedOutContestantsComponent,
        ContestantListItemComponent,
        MatchOpponentComponent,
        PathViewComponent,
        MatchRoundOfComponent,
        DroppedDownComponent,
        DroppedOutComponent,
        FinalRoundComponent,
        NextRoundOnHoldComponent,
        NextRoundComponent,
        FutureMatchesCancelledComponent,
        MatchResultPlayerComponent,
        PathViewSkeletonComponent,
        RoundBoxSkeletonComponent,
        MatchStatusSkeletonComponent,
        MatchContestantsSkeletonComponent,
        MatchSkeletonComponent,
        MatchResultPlayerSkeletonComponent,
        ContestantListItemSkeletonComponent], imports: [CommonModule,
        CommonsLocationModule,
        CommonsComponentModule,
        SvgIconsModule,
        TranslationModule,
        CupPageModule,
        CommonsErrorModule,
        PremiumModule], exports: [PathViewComponent, PathViewSkeletonComponent, ContestantLinkComponent] }); })();
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(PathViewComponentsModule, [{
        type: NgModule,
        args: [{
                imports: [
                    CommonModule,
                    CommonsLocationModule,
                    CommonsComponentModule,
                    SvgIconsModule,
                    TranslationModule,
                    CupPageModule,
                    CommonsErrorModule,
                    PremiumModule,
                ],
                declarations: [
                    MatchLinkComponent,
                    MatchComponent,
                    RoundMessageComponent,
                    ContestantLinkComponent,
                    MatchStatusComponent,
                    MatchContestantsComponent,
                    RoundBoxComponent,
                    PossibleOpponentsComponent,
                    DroppedOutContestantsComponent,
                    ContestantListItemComponent,
                    MatchOpponentComponent,
                    PathViewComponent,
                    MatchRoundOfComponent,
                    DroppedDownComponent,
                    DroppedOutComponent,
                    FinalRoundComponent,
                    NextRoundOnHoldComponent,
                    NextRoundComponent,
                    FutureMatchesCancelledComponent,
                    MatchResultPlayerComponent,
                    PathViewSkeletonComponent,
                    RoundBoxSkeletonComponent,
                    MatchStatusSkeletonComponent,
                    MatchContestantsSkeletonComponent,
                    MatchSkeletonComponent,
                    MatchResultPlayerSkeletonComponent,
                    ContestantListItemSkeletonComponent,
                ],
                exports: [PathViewComponent, PathViewSkeletonComponent, ContestantLinkComponent],
            }]
    }], function () { return [{ type: i1.SvgIconsRepository }]; }, null); })();
i0.ɵɵsetComponentScope(MatchContestantsComponent, [i1$1.NgIf, DroppedOutContestantsComponent,
    PossibleOpponentsComponent,
    MatchOpponentComponent], []);

class PathViewStatsBoxModule {
}
PathViewStatsBoxModule.ɵfac = function PathViewStatsBoxModule_Factory(t) { return new (t || PathViewStatsBoxModule)(); };
PathViewStatsBoxModule.ɵmod = i0.ɵɵdefineNgModule({ type: PathViewStatsBoxModule });
PathViewStatsBoxModule.ɵinj = i0.ɵɵdefineInjector({ providers: [
        {
            provide: STATS_BOX_MAX_DISPLAY,
            useValue: 16,
        },
    ], imports: [[CommonModule, CommonsComponentModule, PathViewComponentsModule, TranslationModule, PremiumModule]] });
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(PathViewStatsBoxModule, { declarations: [StatsBoxComponent, StatsBoxSkeletonComponent], imports: [CommonModule, CommonsComponentModule, PathViewComponentsModule, TranslationModule, PremiumModule], exports: [StatsBoxComponent, StatsBoxSkeletonComponent] }); })();
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(PathViewStatsBoxModule, [{
        type: NgModule,
        args: [{
                imports: [CommonModule, CommonsComponentModule, PathViewComponentsModule, TranslationModule, PremiumModule],
                declarations: [StatsBoxComponent, StatsBoxSkeletonComponent],
                exports: [StatsBoxComponent, StatsBoxSkeletonComponent],
                providers: [
                    {
                        provide: STATS_BOX_MAX_DISPLAY,
                        useValue: 16,
                    },
                ],
            }]
    }], null, null); })();

function CupPagePathViewComponent_ng_container_0_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementContainer(0);
} }
function CupPagePathViewComponent_ng_container_2_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementContainer(0);
} }
function CupPagePathViewComponent_ng_container_4_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementContainer(0);
} }
function CupPagePathViewComponent_ng_template_6_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelement(0, "path-view-stats-box", 4);
    i0.ɵɵelement(1, "path-view", 5);
    i0.ɵɵpipe(2, "async");
    i0.ɵɵpipe(3, "async");
    i0.ɵɵpipe(4, "async");
    i0.ɵɵpipe(5, "async");
    i0.ɵɵpipe(6, "async");
} if (rf & 2) {
    const ctx_r4 = i0.ɵɵnextContext();
    i0.ɵɵproperty("leagueId", ctx_r4.leagueId);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ownContestant", i0.ɵɵpipeBind1(2, 6, ctx_r4.ownContestant$).entry)("selectedParticipant", i0.ɵɵpipeBind1(3, 8, ctx_r4.selectedParticipant$))("league", i0.ɵɵpipeBind1(4, 10, ctx_r4.league$).entry)("participants", i0.ɵɵpipeBind1(5, 12, ctx_r4.participants$).entry)("matches", i0.ɵɵpipeBind1(6, 14, ctx_r4.matches$).entry);
} }
function CupPagePathViewComponent_ng_template_8_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelement(0, "path-view-stats-box-skeleton");
    i0.ɵɵelement(1, "path-view-path-view-skeleton");
} }
function CupPagePathViewComponent_ng_template_10_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "inline-error");
    i0.ɵɵtext(1);
    i0.ɵɵpipe(2, "translate");
    i0.ɵɵelementEnd();
} if (rf & 2) {
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate1(" ", i0.ɵɵpipeBind1(2, 1, "commons:data-fetch-error"), " ");
} }
class CupPagePathViewComponent {
    constructor(store) {
        this.store = store;
    }
    ngOnInit() {
        this.store.dispatch(new LoadLeagueOnceAction(this.leagueId));
        this.league$ = this.store.select(getLeague(this.leagueId));
        this.store.dispatch(new LoadParticipantsOnceAction(this.leagueId));
        this.participants$ = this.store.select(getLeagueParticipants(this.leagueId)).pipe(filter$1(exists), map$1(entry => (Object.assign(Object.assign({}, entry), { entry: entry.entry ? keyBy(entry.entry.filter(isCheckedIn), p => p.id) : entry.entry }))));
        const user$ = this.store.select(getUser).pipe(filter$1(user => Boolean(!user.loading || user.entry)), map$1(user => user.entry));
        const participants$ = this.participants$.pipe(filter$1(entryAvailable));
        this.ownContestant$ = combineLatest(user$, participants$).pipe(switchMap(([user, participants]) => {
            if (!user) {
                return [
                    {
                        loading: false,
                        entry: null,
                        error: null,
                    },
                ];
            }
            this.store.dispatch(new LoadContestantsOnceAction(this.leagueId, +user.id));
            return this.store.select(getContestants(this.leagueId, +user.id)).pipe(filter$1(exists), map$1(contestants => {
                const isParticipating = contestants.entry && contestants.entry.length && participants[contestants.entry[0].id];
                const isLoading = Boolean(contestants.loading && !contestants.entry);
                return Object.assign(Object.assign({}, contestants), { loading: isLoading, entry: isParticipating ? +contestants.entry[0].id : null });
            }));
        }));
        this.store.dispatch(new InitSelectedParticipantAction(this.leagueId));
        this.selectedParticipant$ = this.store.pipe(getSelectedParticipant(this.leagueId), filter$1(Boolean));
        this.store.dispatch(new LoadLeagueResultsOnceAction(this.leagueId));
        this.matches$ = this.store.select(getLeagueResults(this.leagueId)).pipe(filter$1(exists), map$1(entry => (Object.assign(Object.assign({}, entry), { entry: entry.entry ? sortBy(entry.entry, ["round", "position", "bracket"]) : entry.entry }))));
        this.isLoading$ = this.getIsLoading();
        this.hasError$ = this.getHasError();
        this.isReady$ = this.getIsReady();
    }
    getIsReady() {
        return combineLatest(this.isLoading$, this.hasError$).pipe(map$1(([loading, error]) => Boolean(!loading && !error)));
    }
    getHasError() {
        return combineLatest(this.league$, this.participants$, this.ownContestant$, this.matches$).pipe(map$1(data => {
            return data.filter(entry => Boolean(entry && entry.error)).length > 0;
        }));
    }
    getIsLoading() {
        return combineLatest(this.league$, this.participants$, this.ownContestant$, this.matches$).pipe(map$1(data => {
            return data.filter(entry => Boolean(!entry || (entry.loading && !entry.entry))).length !== 0;
        }));
    }
}
CupPagePathViewComponent.ɵfac = function CupPagePathViewComponent_Factory(t) { return new (t || CupPagePathViewComponent)(i0.ɵɵdirectiveInject(i1$2.Store)); };
CupPagePathViewComponent.ɵcmp = i0.ɵɵdefineComponent({ type: CupPagePathViewComponent, selectors: [["path-view-cup-page"]], inputs: { leagueId: "leagueId" }, decls: 12, vars: 12, consts: [[4, "ngIf", "ngIfThen"], ["ready", ""], ["loading", ""], ["error", ""], [3, "leagueId"], [3, "ownContestant", "selectedParticipant", "league", "participants", "matches"]], template: function CupPagePathViewComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵtemplate(0, CupPagePathViewComponent_ng_container_0_Template, 1, 0, "ng-container", 0);
        i0.ɵɵpipe(1, "async");
        i0.ɵɵtemplate(2, CupPagePathViewComponent_ng_container_2_Template, 1, 0, "ng-container", 0);
        i0.ɵɵpipe(3, "async");
        i0.ɵɵtemplate(4, CupPagePathViewComponent_ng_container_4_Template, 1, 0, "ng-container", 0);
        i0.ɵɵpipe(5, "async");
        i0.ɵɵtemplate(6, CupPagePathViewComponent_ng_template_6_Template, 7, 16, "ng-template", null, 1, i0.ɵɵtemplateRefExtractor);
        i0.ɵɵtemplate(8, CupPagePathViewComponent_ng_template_8_Template, 2, 0, "ng-template", null, 2, i0.ɵɵtemplateRefExtractor);
        i0.ɵɵtemplate(10, CupPagePathViewComponent_ng_template_10_Template, 3, 3, "ng-template", null, 3, i0.ɵɵtemplateRefExtractor);
    } if (rf & 2) {
        const _r3 = i0.ɵɵreference(7);
        const _r5 = i0.ɵɵreference(9);
        const _r7 = i0.ɵɵreference(11);
        i0.ɵɵproperty("ngIf", i0.ɵɵpipeBind1(1, 6, ctx.isReady$))("ngIfThen", _r3);
        i0.ɵɵadvance(2);
        i0.ɵɵproperty("ngIf", i0.ɵɵpipeBind1(3, 8, ctx.isLoading$))("ngIfThen", _r5);
        i0.ɵɵadvance(2);
        i0.ɵɵproperty("ngIf", i0.ɵɵpipeBind1(5, 10, ctx.hasError$))("ngIfThen", _r7);
    } }, directives: [i1$1.NgIf, StatsBoxComponent, PathViewComponent, StatsBoxSkeletonComponent, PathViewSkeletonComponent, i1.InlineErrorComponent], pipes: [i1$1.AsyncPipe, i3.TranslatePipe], encapsulation: 2, changeDetection: 0 });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(CupPagePathViewComponent, [{
        type: Component,
        args: [{
                selector: "path-view-cup-page",
                templateUrl: "./cup-page-path-view.component.html",
                encapsulation: ViewEncapsulation.Emulated,
                changeDetection: ChangeDetectionStrategy.OnPush,
            }]
    }], function () { return [{ type: i1$2.Store }]; }, { leagueId: [{
            type: Input
        }] }); })();

class PathViewModule {
}
PathViewModule.ɵfac = function PathViewModule_Factory(t) { return new (t || PathViewModule)(); };
PathViewModule.ɵmod = i0.ɵɵdefineNgModule({ type: PathViewModule });
PathViewModule.ɵinj = i0.ɵɵdefineInjector({ providers: [
        MatchService,
        DoubleEliminationService,
        TournamentService,
        RoundService,
        RoundBuilderService,
        {
            provide: MATCH_CONTESTANTS_MAX_DISPLAY,
            useValue: 8,
        },
    ], imports: [[
            CommonModule,
            TranslationModule,
            CommonsErrorModule,
            StateModule,
            CupPageModule,
            PathViewComponentsModule,
            PathViewStatsBoxModule,
        ]] });
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(PathViewModule, { declarations: [CupPagePathViewComponent], imports: [CommonModule,
        TranslationModule,
        CommonsErrorModule,
        StateModule,
        CupPageModule,
        PathViewComponentsModule,
        PathViewStatsBoxModule], exports: [CupPagePathViewComponent] }); })();
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(PathViewModule, [{
        type: NgModule,
        args: [{
                imports: [
                    CommonModule,
                    TranslationModule,
                    CommonsErrorModule,
                    StateModule,
                    CupPageModule,
                    PathViewComponentsModule,
                    PathViewStatsBoxModule,
                ],
                providers: [
                    MatchService,
                    DoubleEliminationService,
                    TournamentService,
                    RoundService,
                    RoundBuilderService,
                    {
                        provide: MATCH_CONTESTANTS_MAX_DISPLAY,
                        useValue: 8,
                    },
                ],
                declarations: [CupPagePathViewComponent],
                exports: [CupPagePathViewComponent],
            }]
    }], null, null); })();

/*
 * Public API Surface of path-view
 */

/**
 * Generated bundle index. Do not edit.
 */

export { ContestantLinkComponent, ContestantListItemComponent, ContestantListItemSkeletonComponent, CupPagePathViewComponent, DoubleEliminationService, DroppedDownComponent, DroppedOutComponent, DroppedOutContestantsComponent, FinalRoundComponent, FutureMatchesCancelledComponent, MATCH_CONTESTANTS_MAX_DISPLAY, MatchComponent, MatchContestantsComponent, MatchContestantsSkeletonComponent, MatchLinkComponent, MatchOpponentComponent, MatchResultPlayerComponent, MatchResultPlayerSkeletonComponent, MatchRoundOfComponent, MatchService, MatchSkeletonComponent, MatchStatusComponent, MatchStatusSkeletonComponent, NextRoundComponent, NextRoundOnHoldComponent, PathViewComponent, PathViewModule, PathViewSkeletonComponent, PathViewStatsBoxModule, PossibleOpponentsComponent, RoundBoxComponent, RoundBoxSkeletonComponent, RoundBuilderService, RoundMessageComponent, RoundService, RoundState, STATS_BOX_MAX_DISPLAY, StatsBoxComponent, StatsBoxSkeletonComponent, TournamentInformation, TournamentService };
//# sourceMappingURL=play-path-view.js.map
